import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Lock,
  PenLine,
  Pencil,
  Check,
  XCircle,
  Lightbulb,
  MessageCircle,
  ArrowLeft,
  Plus,
  FilePlus2,
  GripVertical,
  Loader2,
  Eye,
  EyeOff,
} from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '../../lib/supabase';
import ThoughtModal from '../Thoughts/ThoughtModal';
import SiteContentDetailModal, {
  type SiteContentDetail,
  type SiteContentDetailSave,
} from './SiteContentDetailModal';
import StageIdeaPickerModal from './StageIdeaPickerModal';
import TimelineDayModal, { type TimelineDayItem } from './TimelineDayModal';

const BOARD_SLUG = 'narrative-arcs';
const EXPANDED_STORAGE_KEY = 'lccto:narrative-arcs:expanded';
const PRIMARY_STORAGE_KEY = 'lccto:narrative-arcs:primary';
const stageContentVisibleKey = (arcSlug: string) =>
  `lccto:narrative-arcs:stage-content:${arcSlug}`;

type ViewMode = 'idea' | 'calendar';

type Stage = 'setup' | 'complication' | 'resolution';
type StageContentVisible = Record<Stage, boolean>;

const DEFAULT_STAGE_CONTENT_VISIBLE: StageContentVisible = {
  setup: true,
  complication: true,
  resolution: true,
};

function readStageContentVisible(arcSlug: string): StageContentVisible {
  try {
    const raw = localStorage.getItem(stageContentVisibleKey(arcSlug));
    if (!raw) return { ...DEFAULT_STAGE_CONTENT_VISIBLE };
    const parsed = JSON.parse(raw) as Partial<StageContentVisible>;
    return {
      setup: parsed.setup !== false,
      complication: parsed.complication !== false,
      resolution: parsed.resolution !== false,
    };
  } catch {
    return { ...DEFAULT_STAGE_CONTENT_VISIBLE };
  }
}

function writeStageContentVisible(arcSlug: string, value: StageContentVisible) {
  try {
    localStorage.setItem(stageContentVisibleKey(arcSlug), JSON.stringify(value));
  } catch {
    /* ignore quota */
  }
}

// Placement confidence on the arc (from thoughts.arc_placement):
// suggested → ghost (proposed fit); tentative → on arc but movable; locked → confirmed.
// Display aliases: incoming / winnowing / settled. Only locked *material* moves the maturity line.
type ElementStatus = 'incoming' | 'winnowing' | 'settled';
type ArcPlacement = 'suggested' | 'tentative' | 'locked';

type PublishStatus = 'idea' | 'planned' | 'published';

interface ThoughtPublication {
  id: string;
  content_id: string;
  title: string | null;
  url: string | null;
  pub_date: string | null;
  scheduled_for: string | null;
  status: string | null;
}

interface ArcElement {
  date?: string;
  /** planned | published — for timeline coloring */
  dateKind?: 'planned' | 'published' | 'capture';
  label: string;
  kind?: string;
  path?: string;
  stage?: Stage;
  status?: ElementStatus;
  placement?: ArcPlacement;
  abstract?: string | null;
  fitNote?: string | null;
  reference?: string | null;
  publishStatus?: PublishStatus;
  publications?: ThoughtPublication[];
  /** When set, this marker comes from the thoughts table (joined by arc_slug). */
  thought_id?: string;
  /** When set, matured site_content row (joined by arc_slug). */
  site_content_id?: string;
  /** Parent idea for timeline ↔ stage highlight. */
  source_thought_id?: number;
  content_id?: string;
  lane?: ArcLaneKind;
  /** Same-day order on the timeline (site_content.schedule_order). */
  schedule_order?: number;
}

interface ThoughtArcRow {
  id: number;
  title: string | null;
  seed_idea: string | null;
  reference: string | null;
  arc_slug: string | null;
  arc_stage: string | null;
  arc_placement: string | null;
  arc_fit_note: string | null;
  first_published_at: string | null;
  status: string | null;
  created_at: string;
}

interface SiteContentArcRow {
  id: string;
  content_id: string;
  title: string | null;
  summary: string | null;
  url: string | null;
  pub_date: string | null;
  scheduled_for: string | null;
  schedule_order: number | null;
  status: string | null;
  origin: string | null;
  arc_slug: string | null;
  arc_stage: string | null;
  source_thought_id: number | null;
}

const SITE_CONTENT_ARC_SELECT =
  'id, content_id, title, summary, url, pub_date, scheduled_for, schedule_order, status, origin, arc_slug, arc_stage, source_thought_id';

function siteDetailFromRow(
  row: SiteContentArcRow,
  arcLabel?: string | null,
): SiteContentDetail {
  return {
    id: row.id,
    content_id: row.content_id,
    title: row.title,
    summary: row.summary,
    url: row.url,
    pub_date: row.pub_date,
    scheduled_for: row.scheduled_for,
    status: row.status,
    arc_slug: row.arc_slug,
    arc_stage: row.arc_stage,
    source_thought_id: row.source_thought_id,
    arc_label: arcLabel ?? null,
  };
}

/** Display lane on the arc — not map-node annotations. */
type ArcLaneKind = 'thought' | 'site_via_engine' | 'site_only';

const KIND_ICON: Record<string, typeof Lock> = {
  published: Lock,
  draft: PenLine,
  live: MessageCircle,
  'idea-placeholder': Lightbulb,
};

function statusOf(e: ArcElement): ElementStatus {
  return e.status ?? 'settled';
}

interface ArcAnnotations {
  maturity?: string;
  elements?: ArcElement[];
  spun_off_from?: string;
  triggered_by?: string;
}

interface ArcNodeRow {
  id: string;
  slug: string;
  label: string;
  role_description: string | null;
  usage_notes: string | null;
  annotations: ArcAnnotations | null;
  y: number;
}

interface DatedElement extends ArcElement {
  ts: number;
}

interface Domain {
  start: number;
  end: number;
}

const MATURITY_META: Record<string, { label: string; pillClass: string }> = {
  holding: { label: 'Holding', pillClass: 'bg-blue-600 dark:bg-blue-500 text-white' },
  framed_unshipped: {
    label: 'Framed, unshipped',
    pillClass: 'border border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300',
  },
  unconfirmed: {
    label: 'Unconfirmed',
    pillClass: 'border border-dashed border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400',
  },
};
const FALLBACK_PILL = 'border border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300';

function maturityMeta(raw: string | undefined) {
  if (raw && MATURITY_META[raw]) return MATURITY_META[raw];
  if (!raw) return { label: 'Unlabeled', pillClass: FALLBACK_PILL };
  return { label: raw.replace(/_/g, ' '), pillClass: FALLBACK_PILL };
}

function formatShortDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
}

function datedElements(elements: ArcElement[], todayMs: number): DatedElement[] {
  return elements
    .map((e) => ({ ...e, ts: e.date ? Date.parse(e.date) : todayMs }))
    .sort((a, b) => a.ts - b.ts);
}

// --- Idea mode: NOT time-based. Position = which narrative stage an element belongs to —
// setup, complication, resolution — not when it was written. The curve is a compact "shape of
// progress" header; the actual reading happens in three columns below it, one per stage, so any
// number of elements sharing a stage stack cleanly instead of colliding on top of each other.
const IDEA_VIEW = { w: 1000, h: 130 };
const IP0 = { x: 20, y: 108 };
const IP1 = { x: 500, y: 18 };
const IP2 = { x: 980, y: 72 };

const STAGE_ORDER: Stage[] = ['setup', 'complication', 'resolution'];
const STAGE_T: Record<Stage, number> = { setup: 0.12, complication: 0.5, resolution: 0.88 };
const STAGE_LABEL: Record<Stage, string> = { setup: 'Setup', complication: 'Complication', resolution: 'Resolution' };

/** Timeline content chip colors by arc segment — solid (no see-through when stacked). */
const STAGE_CHIP: Record<
  Stage,
  { bg: string; border: string; borderHover: string; title: string; meta: string }
> = {
  setup: {
    bg: 'bg-sky-100 dark:bg-sky-950',
    border: 'border-sky-300 dark:border-sky-700',
    borderHover: 'border-sky-500 dark:border-sky-400 ring-2 ring-sky-400/50',
    title: 'text-sky-900 dark:text-sky-100',
    meta: 'text-sky-700 dark:text-sky-300',
  },
  complication: {
    bg: 'bg-amber-100 dark:bg-amber-950',
    border: 'border-amber-300 dark:border-amber-700',
    borderHover: 'border-amber-500 dark:border-amber-400 ring-2 ring-amber-400/50',
    title: 'text-amber-950 dark:text-amber-50',
    meta: 'text-amber-800 dark:text-amber-200',
  },
  resolution: {
    bg: 'bg-emerald-100 dark:bg-emerald-950',
    border: 'border-emerald-300 dark:border-emerald-700',
    borderHover: 'border-emerald-500 dark:border-emerald-400 ring-2 ring-emerald-400/50',
    title: 'text-emerald-950 dark:text-emerald-50',
    meta: 'text-emerald-800 dark:text-emerald-200',
  },
};

function stageOfElement(el: ArcElement): Stage {
  const raw = (el.stage ?? 'complication') as Stage;
  return STAGE_ORDER.includes(raw) ? raw : 'complication';
}

/** Coverage-assisted maturity suggestion (manual pill still wins on the node). */
function suggestMaturityFromCoverage(
  thoughts: ThoughtArcRow[],
  site: SiteContentArcRow[],
): 'unconfirmed' | 'holding' | 'framed_unshipped' {
  const locked = new Set<Stage>();
  const published = new Set<Stage>();
  let pubCount = 0;
  for (const t of thoughts) {
    const raw = (t.arc_stage ?? 'complication') as Stage;
    const stage = STAGE_ORDER.includes(raw) ? raw : 'complication';
    if ((t.arc_placement ?? '') === 'locked') locked.add(stage);
    if (t.first_published_at || t.status === 'completed') published.add(stage);
  }
  for (const s of site) {
    if ((s.status ?? '').toLowerCase() === 'published' && s.pub_date) {
      pubCount += 1;
      const raw = (s.arc_stage ?? 'complication') as Stage;
      const stage = STAGE_ORDER.includes(raw) ? raw : 'complication';
      published.add(stage);
    }
  }
  if (published.size >= 2 || pubCount >= 2) return 'holding';
  if (locked.size >= 2 || published.size >= 1) return 'framed_unshipped';
  return 'unconfirmed';
}

function stageOf(e: ArcElement): Stage {
  return e.stage ?? 'complication';
}
function isMaterial(e: ArcElement): boolean {
  return e.kind === 'draft' || e.kind === 'published' || e.kind === 'live';
}

function placementOf(raw: string | null | undefined): ArcPlacement {
  const p = (raw ?? 'tentative').toLowerCase();
  if (p === 'suggested' || p === 'tentative' || p === 'locked') return p;
  return 'tentative';
}

function placementToElementStatus(placement: ArcPlacement): ElementStatus {
  if (placement === 'suggested') return 'incoming';
  if (placement === 'tentative') return 'winnowing';
  return 'settled';
}

function pubsForThought(
  thoughtId: number,
  byThought: Map<number, ThoughtPublication[]>,
): ThoughtPublication[] {
  return byThought.get(thoughtId) ?? [];
}

function thoughtPublishStatus(
  t: ThoughtArcRow,
  pubs: ThoughtPublication[],
): PublishStatus {
  const published = pubs.filter(
    (p) => (p.status ?? '').toLowerCase() === 'published' && p.pub_date,
  );
  if (published.length > 0 || t.first_published_at) return 'published';
  const hasDraft = pubs.some((p) => (p.status ?? '').toLowerCase() === 'draft');
  if (hasDraft) return 'planned';
  return 'idea';
}

function thoughtTimelineDate(
  t: ThoughtArcRow,
  pubs: ThoughtPublication[],
): { date?: string; dateKind?: 'planned' | 'published' } {
  const published = pubs
    .filter((p) => (p.status ?? '').toLowerCase() === 'published' && p.pub_date)
    .map((p) => p.pub_date!.slice(0, 10))
    .sort();
  if (t.first_published_at) {
    return { date: t.first_published_at.slice(0, 10), dateKind: 'published' };
  }
  if (published[0]) return { date: published[0], dateKind: 'published' };
  const scheduled = pubs
    .filter((p) => (p.status ?? '').toLowerCase() === 'draft' && p.scheduled_for)
    .map((p) => p.scheduled_for!.slice(0, 10))
    .sort();
  if (scheduled[0]) return { date: scheduled[0], dateKind: 'planned' };
  return {};
}

function thoughtToElement(
  t: ThoughtArcRow,
  byThought: Map<number, ThoughtPublication[]>,
): ArcElement {
  const status = (t.status ?? 'draft').toLowerCase();
  const pubs = pubsForThought(t.id, byThought);
  const publishStatus = thoughtPublishStatus(t, pubs);
  const kind =
    publishStatus === 'published'
      ? 'published'
      : status === 'promoted'
        ? 'draft'
        : 'idea-placeholder';
  const placement = placementOf(t.arc_placement);
  const stageRaw = (t.arc_stage ?? 'complication').toLowerCase();
  const stage: Stage = STAGE_ORDER.includes(stageRaw as Stage) ? (stageRaw as Stage) : 'complication';
  const timeline = thoughtTimelineDate(t, pubs);
  return {
    date: timeline.date,
    dateKind: timeline.dateKind,
    label: t.title?.trim() || 'Untitled thought',
    kind,
    stage,
    status: placementToElementStatus(placement),
    placement,
    abstract: t.seed_idea,
    fitNote: t.arc_fit_note,
    reference: t.reference,
    publishStatus,
    publications: pubs,
    path: '/thoughts',
    thought_id: String(t.id),
    lane: 'thought',
  };
}

function siteContentToElement(
  row: SiteContentArcRow,
  /** Prefer the parent idea’s live stage over a stale snapshot on the content row. */
  thoughtStageById?: Map<number, Stage>,
): ArcElement {
  const status = (row.status ?? 'published').toLowerCase();
  const fromThought =
    row.source_thought_id != null
      ? thoughtStageById?.get(row.source_thought_id)
      : undefined;
  const stageRaw = (fromThought ?? row.arc_stage ?? 'complication').toLowerCase();
  const stage: Stage = STAGE_ORDER.includes(stageRaw as Stage) ? (stageRaw as Stage) : 'complication';
  const viaEngine = row.source_thought_id != null;
  const isDraft = status === 'draft';
  const date = isDraft
    ? row.scheduled_for ?? undefined
    : row.pub_date ?? row.scheduled_for ?? undefined;
  return {
    date,
    dateKind: status === 'published' && row.pub_date ? 'published' : date ? 'planned' : undefined,
    label: row.title?.trim() || row.content_id,
    kind: status === 'published' ? 'published' : 'draft',
    stage,
    status: isDraft ? 'winnowing' : 'settled',
    placement: isDraft ? 'tentative' : 'locked',
    abstract: row.summary,
    fitNote: isDraft
      ? 'Draft content stub — writing + schedule (dates movable).'
      : viaEngine
        ? 'Published post linked from an Engine idea.'
        : 'Published site material on this arc.',
    reference: row.url,
    publishStatus: status === 'published' ? 'published' : isDraft ? 'planned' : 'idea',
    path: row.url || undefined,
    site_content_id: row.id,
    content_id: row.content_id,
    source_thought_id: row.source_thought_id ?? undefined,
    lane: viaEngine ? 'site_via_engine' : 'site_only',
    schedule_order: row.schedule_order ?? 0,
  };
}

/** Thought ids linked from timeline content (for stage-panel highlight). */
function linkedThoughtIdsFromElements(items: ArcElement[]): string[] {
  const ids = new Set<string>();
  for (const el of items) {
    if (el.source_thought_id != null) ids.add(String(el.source_thought_id));
    else if (el.thought_id) ids.add(el.thought_id);
  }
  return [...ids];
}

function readStoredExpanded(): Set<string> {
  try {
    const raw = localStorage.getItem(EXPANDED_STORAGE_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((x): x is string => typeof x === 'string'));
  } catch {
    return new Set();
  }
}

function buildPubsByThought(siteRows: SiteContentArcRow[]): Map<number, ThoughtPublication[]> {
  const map = new Map<number, ThoughtPublication[]>();
  for (const row of siteRows) {
    if (row.source_thought_id == null) continue;
    const pub: ThoughtPublication = {
      id: row.id,
      content_id: row.content_id,
      title: row.title,
      url: row.url,
      pub_date: row.pub_date,
      scheduled_for: row.scheduled_for,
      status: row.status,
    };
    const list = map.get(row.source_thought_id) ?? [];
    list.push(pub);
    map.set(row.source_thought_id, list);
  }
  return map;
}

/**
 * Idea view: thoughts only (content as sub-dots under each idea).
 * Timeline: content only (draft/published site_content with a schedule or pub date).
 */
function mergeArcElements(
  thoughts: ThoughtArcRow[],
  siteRows: SiteContentArcRow[],
  mode: ViewMode,
): ArcElement[] {
  const byThought = buildPubsByThought(siteRows);
  const thoughtEls = thoughts.map((t) => thoughtToElement(t, byThought));

  if (mode === 'idea') {
    return thoughtEls;
  }

  // Timeline: content pieces only — not ideas. Same-day order via schedule_order.
  // Color by the idea’s current stage when linked (content.arc_stage can lag after drag).
  const thoughtStageById = new Map<number, Stage>();
  for (const t of thoughts) {
    const raw = (t.arc_stage ?? 'complication').toLowerCase();
    thoughtStageById.set(
      t.id,
      STAGE_ORDER.includes(raw as Stage) ? (raw as Stage) : 'complication',
    );
  }
  return siteRows
    .filter((r) => Boolean(r.scheduled_for || r.pub_date))
    .map((r) => siteContentToElement(r, thoughtStageById))
    .sort((a, b) => {
      const da = a.date?.slice(0, 10) ?? '';
      const db = b.date?.slice(0, 10) ?? '';
      if (da !== db) return da.localeCompare(db);
      return (a.schedule_order ?? 0) - (b.schedule_order ?? 0);
    });
}

function ContentSubDots({ pubs }: { pubs: ThoughtPublication[] }) {
  if (pubs.length === 0) {
    return (
      <span
        className="inline-block w-1.5 h-1.5 rounded-full border border-dashed border-gray-300 dark:border-gray-600"
        title="Seed only — no content yet"
      />
    );
  }
  return (
    <span className="inline-flex items-center gap-0.5" title={`${pubs.length} content piece(s)`}>
      {pubs.map((p) => {
        const st = (p.status ?? '').toLowerCase();
        const published = st === 'published' && p.pub_date;
        return (
          <span
            key={p.id}
            className={
              published
                ? 'inline-block w-1.5 h-1.5 rounded-full bg-blue-600 dark:bg-blue-400'
                : 'inline-block w-1.5 h-1.5 rounded-full border border-blue-400 dark:border-blue-500 bg-white dark:bg-gray-800'
            }
            title={
              published
                ? `Published ${p.pub_date}`
                : p.scheduled_for
                  ? `Draft · ${p.scheduled_for}`
                  : 'Draft'
            }
          />
        );
      })}
    </span>
  );
}

function publishStatusLabel(s?: PublishStatus): string {
  if (s === 'published') return 'published';
  if (s === 'planned') return 'planned';
  return 'idea';
}

/** Counts toward the arc's maturity/solid-line — real material AND settled, not still being sorted. */
function countsTowardMaturity(e: ArcElement): boolean {
  return isMaterial(e) && statusOf(e) === 'settled';
}

function ideaPoint(t: number) {
  const mt = 1 - t;
  return { x: mt * mt * IP0.x + 2 * mt * t * IP1.x + t * t * IP2.x, y: mt * mt * IP0.y + 2 * mt * t * IP1.y + t * t * IP2.y };
}
function ideaPath(t0: number, t1: number, steps = 24) {
  const pts = Array.from({ length: steps + 1 }, (_, i) => ideaPoint(t0 + (t1 - t0) * (i / steps)));
  return pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');
}

function groupByStage(elements: ArcElement[]): Record<Stage, ArcElement[]> {
  const grouped: Record<Stage, ArcElement[]> = { setup: [], complication: [], resolution: [] };
  elements.forEach((e) => grouped[stageOf(e)].push(e));
  return grouped;
}

function ideaLayout(elements: ArcElement[]): { grouped: Record<Stage, ArcElement[]>; solidT: number } {
  const grouped = groupByStage(elements);
  let reachedIdx = -1;
  STAGE_ORDER.forEach((stage, i) => {
    if (grouped[stage].some(countsTowardMaturity)) reachedIdx = i;
  });
  const solidT = reachedIdx >= 0 ? STAGE_T[STAGE_ORDER[reachedIdx]] : 0.02;
  return { grouped, solidT };
}

// --- Calendar: zoom + continuous slide; today at 20% left when panDays=0.
const CAL_VIEW_W = 1000;
const CAL_X0 = 40;
const CAL_X1 = 960;
const CAL_AXIS_Y = 0.58; // fraction of view height — room above for splay stacks
const DAY_MS = 86400000;
/** Today sits at this fraction of the usable track (left → right) when panDays is 0. */
const TODAY_FRAC = 0.2;
const CLICK_VS_DRAG_THRESHOLD_PX = 5;

type TimelineZoom = 'days' | 'weeks' | 'months' | 'quarters' | 'year';

const TIMELINE_ZOOM: Record<
  TimelineZoom,
  { label: string; spanDays: number; stepDays: number }
> = {
  days: { label: 'Days', spanDays: 14, stepDays: 2 },
  weeks: { label: 'Weeks', spanDays: 56, stepDays: 7 },
  months: { label: 'Months', spanDays: 120, stepDays: 14 },
  quarters: { label: 'Quarters', spanDays: 200, stepDays: 30 },
  year: { label: 'Year', spanDays: 365, stepDays: 30 },
};

const TIMELINE_ZOOM_ORDER: TimelineZoom[] = [
  'days',
  'weeks',
  'months',
  'quarters',
  'year',
];

/** major = labeled; month/week/day = divider marks (smaller). */
type AxisTick = {
  ts: number;
  label: string;
  dayIso: string;
  rank: 'major' | 'month' | 'week' | 'day';
};

function calendarX(ts: number, domain: Domain) {
  const span = Math.max(domain.end - domain.start, 1);
  return CAL_X0 + (CAL_X1 - CAL_X0) * Math.min(1, Math.max(0, (ts - domain.start) / span));
}

function localNoon(y: number, m0: number, d: number): number {
  return new Date(y, m0, d, 12, 0, 0, 0).getTime();
}

function dayIsoFromParts(y: number, m0: number, d: number): string {
  const m = String(m0 + 1).padStart(2, '0');
  const day = String(d).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function startOfWeekMonday(from: Date): Date {
  const c = new Date(from);
  c.setHours(0, 0, 0, 0);
  const day = c.getDay(); // 0 = Sun
  const diff = day === 0 ? -6 : 1 - day;
  c.setDate(c.getDate() + diff);
  return c;
}

function pushTick(
  map: Map<string, AxisTick>,
  tick: AxisTick,
  rankWeight: Record<AxisTick['rank'], number>,
) {
  const existing = map.get(tick.dayIso);
  if (!existing || rankWeight[tick.rank] >= rankWeight[existing.rank]) {
    map.set(tick.dayIso, tick);
  }
}

function eachDayInDomain(
  domain: Domain,
  fn: (y: number, m0: number, d: number, date: Date) => void,
) {
  const cursor = new Date(domain.start);
  cursor.setHours(0, 0, 0, 0);
  while (cursor.getTime() <= domain.end) {
    fn(cursor.getFullYear(), cursor.getMonth(), cursor.getDate(), cursor);
    cursor.setDate(cursor.getDate() + 1);
  }
}

function eachWeekStartInDomain(
  domain: Domain,
  fn: (y: number, m0: number, d: number, date: Date) => void,
) {
  const cursor = startOfWeekMonday(new Date(domain.start));
  while (cursor.getTime() <= domain.end + DAY_MS) {
    if (cursor.getTime() >= domain.start - DAY_MS) {
      fn(cursor.getFullYear(), cursor.getMonth(), cursor.getDate(), cursor);
    }
    cursor.setDate(cursor.getDate() + 7);
  }
}

function eachMonthStartInDomain(
  domain: Domain,
  fn: (y: number, m0: number) => void,
) {
  const start = new Date(domain.start);
  let y = start.getFullYear();
  let m0 = start.getMonth();
  while (true) {
    const ts = localNoon(y, m0, 1);
    if (ts > domain.end) break;
    if (ts >= domain.start - DAY_MS) fn(y, m0);
    m0 += 1;
    if (m0 > 11) {
      m0 = 0;
      y += 1;
    }
  }
}

/** Window where real today is at TODAY_FRAC when panDays=0; panDays slides continuously. */
function timelineViewDomain(
  todayMs: number,
  zoom: TimelineZoom,
  panDays: number,
): Domain {
  const cfg = TIMELINE_ZOOM[zoom];
  const spanMs = cfg.spanDays * DAY_MS;
  const baseStart = todayMs - TODAY_FRAC * spanMs;
  const start = baseStart + panDays * DAY_MS;
  return { start, end: start + spanMs };
}

/**
 * Axis ticks by zoom:
 * - Days: labeled days
 * - Weeks: day tick marks + labeled week starts
 * - Months: week tick marks + labeled months
 * - Quarters: week + month ticks + labeled quarters
 * - Year: labeled months
 */
function timelineAxisTicks(domain: Domain, zoom: TimelineZoom): AxisTick[] {
  const rankWeight: Record<AxisTick['rank'], number> = {
    day: 1,
    week: 2,
    month: 3,
    major: 4,
  };
  const map = new Map<string, AxisTick>();

  if (zoom === 'days') {
    eachDayInDomain(domain, (y, m0, d, date) => {
      // Label every other day so the axis stays readable.
      const labelEvery = d % 2 === 1;
      pushTick(map, {
        ts: localNoon(y, m0, d),
        dayIso: dayIsoFromParts(y, m0, d),
        rank: labelEvery ? 'major' : 'day',
        label: labelEvery
          ? date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
          : '',
      }, rankWeight);
    });
    return [...map.values()].sort((a, b) => a.ts - b.ts);
  }

  if (zoom === 'weeks') {
    // Day tick marks across the window.
    eachDayInDomain(domain, (y, m0, d) => {
      pushTick(map, {
        ts: localNoon(y, m0, d),
        dayIso: dayIsoFromParts(y, m0, d),
        rank: 'day',
        label: '',
      }, rankWeight);
    });
    // Week starts get the labels.
    eachWeekStartInDomain(domain, (y, m0, d, date) => {
      pushTick(map, {
        ts: localNoon(y, m0, d),
        dayIso: dayIsoFromParts(y, m0, d),
        rank: 'major',
        label: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      }, rankWeight);
    });
    return [...map.values()].sort((a, b) => a.ts - b.ts);
  }

  if (zoom === 'months') {
    // Week dividers.
    eachWeekStartInDomain(domain, (y, m0, d) => {
      pushTick(map, {
        ts: localNoon(y, m0, d),
        dayIso: dayIsoFromParts(y, m0, d),
        rank: 'week',
        label: '',
      }, rankWeight);
    });
    // Month labels.
    eachMonthStartInDomain(domain, (y, m0) => {
      pushTick(map, {
        ts: localNoon(y, m0, 1),
        dayIso: dayIsoFromParts(y, m0, 1),
        rank: 'major',
        label: new Date(y, m0, 1).toLocaleDateString('en-US', {
          month: 'short',
          year: 'numeric',
        }),
      }, rankWeight);
    });
    return [...map.values()].sort((a, b) => a.ts - b.ts);
  }

  if (zoom === 'quarters') {
    // Week tick marks.
    eachWeekStartInDomain(domain, (y, m0, d) => {
      pushTick(map, {
        ts: localNoon(y, m0, d),
        dayIso: dayIsoFromParts(y, m0, d),
        rank: 'week',
        label: '',
      }, rankWeight);
    });
    // Month tick marks (short label).
    eachMonthStartInDomain(domain, (y, m0) => {
      pushTick(map, {
        ts: localNoon(y, m0, 1),
        dayIso: dayIsoFromParts(y, m0, 1),
        rank: 'month',
        label: new Date(y, m0, 1).toLocaleDateString('en-US', { month: 'short' }),
      }, rankWeight);
    });
    // Quarter majors.
    const start = new Date(domain.start);
    let y = start.getFullYear();
    let q = Math.floor(start.getMonth() / 3);
    while (true) {
      const m0 = q * 3;
      const ts = localNoon(y, m0, 1);
      if (ts > domain.end) break;
      if (ts >= domain.start - DAY_MS) {
        pushTick(map, {
          ts,
          dayIso: dayIsoFromParts(y, m0, 1),
          rank: 'major',
          label: `Q${q + 1} ${y}`,
        }, rankWeight);
      }
      q += 1;
      if (q > 3) {
        q = 0;
        y += 1;
      }
    }
    return [...map.values()].sort((a, b) => a.ts - b.ts);
  }

  // Year → month ticks labeled.
  eachMonthStartInDomain(domain, (y, m0) => {
    pushTick(map, {
      ts: localNoon(y, m0, 1),
      dayIso: dayIsoFromParts(y, m0, 1),
      rank: 'major',
      label: new Date(y, m0, 1).toLocaleDateString('en-US', { month: 'short' }),
    }, rankWeight);
  });
  return [...map.values()].sort((a, b) => a.ts - b.ts);
}

function tickMarkExtents(rank: AxisTick['rank']): { above: number; below: number } {
  switch (rank) {
    case 'major':
      return { above: 10, below: 10 };
    case 'month':
      return { above: 8, below: 7 };
    case 'week':
      return { above: 6, below: 5 };
    case 'day':
    default:
      return { above: 4, below: 3 };
  }
}

/** Full vertical divider lines for month/week boundaries (by zoom). */
function showDividerLine(zoom: TimelineZoom, rank: AxisTick['rank']): boolean {
  if (rank === 'week') return zoom === 'months' || zoom === 'quarters';
  if (rank === 'month') return zoom === 'months' || zoom === 'quarters' || zoom === 'year';
  if (rank === 'major') {
    // Month majors on months/year; quarter majors on quarters.
    return zoom === 'months' || zoom === 'quarters' || zoom === 'year';
  }
  return false;
}

function formatWindowRange(domain: Domain, zoom: TimelineZoom): string {
  const a = new Date(domain.start);
  const b = new Date(domain.end);
  if (zoom === 'quarters' || zoom === 'year' || zoom === 'months') {
    const fmt = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    return `${fmt(a)} – ${fmt(b)}`;
  }
  const fmt = (d: Date) =>
    d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  return `${fmt(a)} – ${fmt(b)}`;
}

function pctOf(view: { w: number; h: number }, p: { x: number; y: number }) {
  return { left: `${(p.x / view.w) * 100}%`, top: `${(p.y / view.h) * 100}%` };
}

type DatedMarker = DatedElement;

type TimelineCluster = {
  /** Anchor date for the stem (first item). */
  ts: number;
  x: number;
  items: DatedMarker[];
};

/** Group by calendar day; within a day keep schedule_order. */
function clusterTimelineMarkers(dated: DatedMarker[], win: Domain): TimelineCluster[] {
  if (dated.length === 0) return [];
  const byDay = new Map<string, DatedMarker[]>();
  for (const item of [...dated].sort((a, b) => {
    const da = a.date?.slice(0, 10) ?? '';
    const db = b.date?.slice(0, 10) ?? '';
    if (da !== db) return da.localeCompare(db) || a.ts - b.ts;
    return (a.schedule_order ?? 0) - (b.schedule_order ?? 0);
  })) {
    const dayKey = item.date?.slice(0, 10) ?? String(item.ts);
    const list = byDay.get(dayKey) ?? [];
    list.push(item);
    byDay.set(dayKey, list);
  }
  const clusters: TimelineCluster[] = [];
  for (const [, items] of byDay) {
    const x =
      items.reduce((sum, m) => sum + calendarX(m.ts, win), 0) / Math.max(items.length, 1);
    clusters.push({ ts: items[0]!.ts, x, items });
  }
  return clusters;
}

/** Max staggered label rows above the axis before titles share a lane. */
const TIMELINE_MAX_LANES = 10;
/** Approximate half-width of a title chip in viewBox units (CAL_VIEW_W = 1000). */
const TIMELINE_LABEL_HALF_W = 110;
/** Vertical gap between staggered title rows (viewBox units). */
const TIMELINE_LANE_STEP = 56;
/** Clearance between the lowest content chip and the time axis. */
const TIMELINE_AXIS_GAP = 52;

function calendarViewHeight(laneCount: number, compact?: boolean): number {
  const lanes = Math.max(1, Math.min(TIMELINE_MAX_LANES, laneCount));
  const labelBand = lanes * TIMELINE_LANE_STEP + TIMELINE_AXIS_GAP + 24;
  const base = compact ? 110 : 140;
  return Math.max(compact ? 260 : 320, base + labelBand);
}

/** Stagger nearby single-day labels into vertical lanes so titles don't collide. */
function assignTimelineLanes(clusters: TimelineCluster[]): number[] {
  const indexed = clusters.map((c, i) => ({ c, i })).sort((a, b) => a.c.x - b.c.x);
  const lanes = new Array(clusters.length).fill(0);
  const laneLastX: number[] = [];
  for (const { c, i } of indexed) {
    let lane = 0;
    while (lane < TIMELINE_MAX_LANES) {
      const last = laneLastX[lane];
      if (last == null || c.x - last >= TIMELINE_LABEL_HALF_W * 2) break;
      lane += 1;
    }
    if (lane >= TIMELINE_MAX_LANES) lane = TIMELINE_MAX_LANES - 1;
    lanes[i] = lane;
    laneLastX[lane] = c.x;
  }
  return lanes;
}

function timelineLabelCrowded(clusters: TimelineCluster[], index: number): boolean {
  const x = clusters[index]?.x ?? 0;
  for (let j = 0; j < clusters.length; j += 1) {
    if (j === index) continue;
    if (Math.abs((clusters[j]?.x ?? 0) - x) < TIMELINE_LABEL_HALF_W * 2) return true;
  }
  return false;
}

function IdeaVisual({
  elements,
  onSelectElement,
  onMoveStage,
  onAddToStage,
  onAddContentForIdea,
  highlightThoughtIds,
  onHighlightThoughts,
  stageContentVisible,
  onToggleStageContent,
  showCurve = true,
}: {
  elements: ArcElement[];
  onSelectElement: (el: ArcElement) => void;
  onMoveStage?: (thoughtId: string, stage: Stage) => void;
  /** Open picker to create or attach an idea into this stage. */
  onAddToStage?: (stage: Stage) => void;
  /** Detail page — open add-content form for this idea. */
  onAddContentForIdea?: (thoughtId: string) => void;
  /** Shared highlight set (timeline ↔ ideas ↔ stage). */
  highlightThoughtIds?: Set<string>;
  /** Detail page — drive timeline highlight from idea/stage hover. */
  onHighlightThoughts?: (thoughtIds: string[]) => void;
  /** Per-stage: show linked content on the timeline (detail). */
  stageContentVisible?: StageContentVisible;
  onToggleStageContent?: (stage: Stage) => void;
  /** Arc curve SVG — hide on detail page where stages sit under the timeline. */
  showCurve?: boolean;
}) {
  const { grouped, solidT } = useMemo(() => ideaLayout(elements), [elements]);
  const [dragOverStage, setDragOverStage] = useState<Stage | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);

  function thoughtIdsForStage(stage: Stage): string[] {
    return grouped[stage]
      .map((el) => el.thought_id)
      .filter((id): id is string => Boolean(id));
  }

  function highlightStage(stage: Stage) {
    onHighlightThoughts?.(thoughtIdsForStage(stage));
  }

  function highlightIdea(thoughtId: string) {
    onHighlightThoughts?.([thoughtId]);
  }

  function clearHighlight() {
    onHighlightThoughts?.([]);
  }

  return (
    <div className={showCurve ? 'mt-3' : ''}>
      {showCurve && (
        <div className="relative w-full" style={{ paddingBottom: `${(IDEA_VIEW.h / IDEA_VIEW.w) * 100}%` }}>
          <svg className="absolute inset-0 w-full h-full" viewBox={`0 0 ${IDEA_VIEW.w} ${IDEA_VIEW.h}`} preserveAspectRatio="xMidYMid meet">
            <path d={ideaPath(0, solidT)} fill="none" strokeWidth={2.5} className="stroke-blue-600 dark:stroke-blue-400" />
            <path
              d={ideaPath(solidT, 0.98)}
              fill="none"
              strokeWidth={2.5}
              strokeDasharray="1 7"
              strokeLinecap="round"
              className="stroke-gray-300 dark:stroke-gray-600"
            />
            {STAGE_ORDER.map((stage) => {
              const items = grouped[stage];
              if (items.length === 0) return null;
              const hasMaterial = items.some(countsTowardMaturity);
              const p = ideaPoint(STAGE_T[stage]);
              return (
                <circle
                  key={stage}
                  cx={p.x}
                  cy={p.y}
                  r={hasMaterial ? 6 : 5.5}
                  strokeWidth={2}
                  className={
                    hasMaterial
                      ? 'fill-blue-600 dark:fill-blue-400 stroke-white dark:stroke-gray-800'
                      : 'fill-white dark:fill-gray-800 stroke-blue-300 dark:stroke-blue-700'
                  }
                  strokeDasharray={hasMaterial ? undefined : '2 2'}
                />
              );
            })}
          </svg>
        </div>
      )}

      <p className="text-[11px] font-mono text-gray-400 dark:text-gray-500 mb-2">
        {showCurve
          ? 'Drag ideas between stages · hollow dots = draft content · solid = published'
          : 'Drag ideas between stages · content count on each card'}
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-4 mt-1">
        {STAGE_ORDER.map((stage) => {
          const items = grouped[stage];
          const over = dragOverStage === stage;
          const stageThoughtIds = thoughtIdsForStage(stage);
          const stageLit =
            stageThoughtIds.length > 0 &&
            Boolean(highlightThoughtIds?.size) &&
            stageThoughtIds.every((id) => highlightThoughtIds!.has(id)) &&
            highlightThoughtIds!.size >= stageThoughtIds.length;
          const contentShown = stageContentVisible?.[stage] !== false;
          return (
            <div
              key={stage}
              onDragOver={(e) => {
                if (!onMoveStage) return;
                e.preventDefault();
                setDragOverStage(stage);
              }}
              onDragLeave={() => setDragOverStage((s) => (s === stage ? null : s))}
              onDrop={(e) => {
                e.preventDefault();
                setDragOverStage(null);
                const id = e.dataTransfer.getData('text/thought-id') || draggingId;
                setDraggingId(null);
                if (id && onMoveStage) onMoveStage(id, stage);
              }}
              onMouseEnter={() => {
                if (onHighlightThoughts) highlightStage(stage);
              }}
              onMouseLeave={() => {
                if (onHighlightThoughts) clearHighlight();
              }}
              className={`group/stage rounded-lg px-2 py-2 min-h-[120px] transition-colors ${
                over
                  ? 'bg-blue-50/80 dark:bg-blue-950/40 ring-1 ring-blue-300 dark:ring-blue-700'
                  : stageLit
                    ? `${STAGE_CHIP[stage].bg} ring-1 ${STAGE_CHIP[stage].border}`
                    : 'bg-transparent hover:bg-gray-50/70 dark:hover:bg-gray-800/50'
              }`}
            >
              <div
                className={`flex items-center gap-2 mb-2 pb-1.5 border-b ${STAGE_CHIP[stage].border}`}
              >
                <div
                  className={`text-[10px] uppercase tracking-wider font-mono min-w-0 flex-1 ${STAGE_CHIP[stage].meta}`}
                >
                  {STAGE_LABEL[stage]}
                  <span className="ml-1.5 opacity-60">{items.length}</span>
                  {!contentShown && (
                    <span className="ml-1.5 normal-case tracking-normal opacity-80">
                      · content hidden
                    </span>
                  )}
                </div>
                {onToggleStageContent && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleStageContent(stage);
                    }}
                    className={`inline-flex items-center gap-0.5 shrink-0 px-1.5 py-0.5 text-[10px] font-medium rounded border transition-colors ${
                      contentShown
                        ? 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 bg-white/90 dark:bg-gray-900/80 hover:bg-gray-50 dark:hover:bg-gray-800'
                        : 'border-amber-300 dark:border-amber-700 text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/40'
                    }`}
                    title={
                      contentShown
                        ? `Hide ${STAGE_LABEL[stage]} content on timeline`
                        : `Show ${STAGE_LABEL[stage]} content on timeline`
                    }
                    aria-pressed={contentShown}
                  >
                    {contentShown ? (
                      <Eye className="w-3 h-3" />
                    ) : (
                      <EyeOff className="w-3 h-3" />
                    )}
                    {contentShown ? 'On' : 'Off'}
                  </button>
                )}
                {onAddToStage && (
                  <button
                    type="button"
                    onClick={() => onAddToStage(stage)}
                    className="opacity-100 sm:opacity-0 sm:group-hover/stage:opacity-100 focus:opacity-100 inline-flex items-center gap-0.5 shrink-0 px-1.5 py-0.5 text-[10px] font-medium rounded border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 bg-white/90 dark:bg-gray-900/80 hover:bg-blue-50 dark:hover:bg-blue-950/50 transition-opacity"
                    title={`Add idea to ${STAGE_LABEL[stage]}`}
                  >
                    <Plus className="w-3 h-3" />
                    Add
                  </button>
                )}
              </div>
              {items.length === 0 ? (
                <div className="space-y-2">
                  <p className="text-[13px] text-gray-300 dark:text-gray-600 italic">
                    {onAddToStage ? 'Empty stage' : 'Drop ideas here'}
                  </p>
                  {onAddToStage && (
                    <button
                      type="button"
                      onClick={() => onAddToStage(stage)}
                      className="text-[12px] text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      Add idea…
                    </button>
                  )}
                </div>
              ) : (
                <ul className="space-y-2">
                  {items.map((el, i) => {
                    const material = isMaterial(el);
                    const status = statusOf(el);
                    const Icon = el.kind ? KIND_ICON[el.kind] : undefined;
                    const fade =
                      !material
                        ? 'opacity-60'
                        : status === 'incoming'
                          ? 'opacity-40'
                          : status === 'winnowing'
                            ? 'opacity-75'
                            : '';
                    const isThought = el.lane === 'thought' && Boolean(el.thought_id);
                    const titleClass = 'text-blue-700 dark:text-blue-300';
                    const pubs = el.publications ?? [];
                    const highlighted =
                      Boolean(el.thought_id) &&
                      Boolean(highlightThoughtIds?.has(el.thought_id!));
                    const showHoverActions = isThought && Boolean(el.thought_id);
                    return (
                      <li
                        key={el.thought_id ?? `${el.label}-${i}`}
                        data-thought-id={el.thought_id ?? undefined}
                        className={`${fade} rounded-md transition-shadow duration-150 ${
                          highlighted
                            ? 'bg-white dark:bg-gray-900 shadow-[0_6px_22px_rgba(37,99,235,0.45)] dark:shadow-[0_6px_22px_rgba(96,165,250,0.4)] z-10 relative'
                            : ''
                        }`}
                        draggable={Boolean(isThought && onMoveStage)}
                        onMouseEnter={() => {
                          if (el.thought_id && onHighlightThoughts) {
                            highlightIdea(el.thought_id);
                          }
                        }}
                        onMouseLeave={() => {
                          // Still inside the stage column — restore stage-wide highlight.
                          if (onHighlightThoughts) highlightStage(stage);
                        }}
                        onDragStart={(e) => {
                          if (!el.thought_id) return;
                          setDraggingId(el.thought_id);
                          e.dataTransfer.setData('text/thought-id', el.thought_id);
                          e.dataTransfer.effectAllowed = 'move';
                        }}
                        onDragEnd={() => {
                          setDraggingId(null);
                          setDragOverStage(null);
                        }}
                      >
                        <div className="group/idea relative rounded-md -mx-1">
                          <div
                            className={`w-full text-left px-1.5 py-1.5 pr-16 rounded-md transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/60 ${
                              isThought && onMoveStage ? 'cursor-grab active:cursor-grabbing' : ''
                            }`}
                          >
                            <div className="flex items-start gap-1.5">
                              {Icon && (
                                <Icon className="w-3 h-3 mt-0.5 text-gray-400 dark:text-gray-500 shrink-0" />
                              )}
                              <div className="min-w-0 flex-1">
                                <div className={`text-[13px] font-medium leading-snug ${titleClass}`}>
                                  {el.label}
                                </div>
                                <div className="flex items-center gap-2 mt-1 flex-wrap">
                                  <span
                                    className="inline-flex items-center gap-1 rounded-full bg-gray-100 dark:bg-gray-700/80 px-1.5 py-0.5"
                                    title={`${pubs.length} content piece(s)`}
                                  >
                                    <ContentSubDots pubs={pubs} />
                                    <span className="text-[10px] font-mono text-gray-600 dark:text-gray-300">
                                      {pubs.length}
                                    </span>
                                  </span>
                                  <span className="text-[10px] font-mono text-gray-400 dark:text-gray-500">
                                    {publishStatusLabel(el.publishStatus)}
                                    {el.placement && el.placement !== 'locked'
                                      ? ` · ${el.placement}`
                                      : el.placement === 'locked'
                                        ? ' · locked'
                                        : ''}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                          {showHoverActions && (
                            <div className="absolute top-1 right-1 flex items-center gap-1 opacity-100 sm:opacity-0 sm:group-hover/idea:opacity-100 focus-within:opacity-100 transition-opacity">
                              <button
                                type="button"
                                title="Edit idea"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onSelectElement(el);
                                }}
                                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white/95 dark:bg-gray-900/90 hover:bg-gray-50 dark:hover:bg-gray-800"
                              >
                                <Pencil className="w-3 h-3" />
                                Edit
                              </button>
                              {onAddContentForIdea && (
                                <button
                                  type="button"
                                  title="Add content to this idea"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onAddContentForIdea(el.thought_id!);
                                  }}
                                  className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium rounded border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 bg-white/95 dark:bg-gray-900/90 hover:bg-blue-50 dark:hover:bg-blue-950/50"
                                >
                                  <FilePlus2 className="w-3 h-3" />
                                  Add
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Draft content stub — drag to change scheduled_for. Published is locked. */
function isReschedulableDraft(el: ArcElement): boolean {
  return Boolean(el.site_content_id) && el.dateKind === 'planned' && el.kind === 'draft';
}

function isReschedulable(el: ArcElement): boolean {
  return isReschedulableDraft(el);
}

function isoDateLocal(ts: number): string {
  const d = new Date(ts);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function noonLocalFromIso(iso: string): number {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y!, (m ?? 1) - 1, d ?? 1, 12, 0, 0).getTime();
}

/** Hover chip on a timeline date — Add content without stacking modals. */
function DateAddHover({
  dayIso,
  label,
  onAdd,
  children,
}: {
  dayIso: string;
  label?: string;
  onAdd?: (dayIso: string) => void;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const leaveTimer = useRef<number | null>(null);

  function clearLeave() {
    if (leaveTimer.current != null) {
      window.clearTimeout(leaveTimer.current);
      leaveTimer.current = null;
    }
  }

  function onEnter() {
    clearLeave();
    setOpen(true);
  }

  function onLeave() {
    clearLeave();
    leaveTimer.current = window.setTimeout(() => setOpen(false), 160);
  }

  if (!onAdd) return <>{children}</>;

  return (
    <div
      className="relative inline-flex flex-col items-center"
      data-no-pan
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      onPointerDown={(e) => e.stopPropagation()}
    >
      {children}
      {open && (
        <div
          className="absolute top-full mt-1 z-[60] whitespace-nowrap rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-lg px-2 py-1.5"
          data-no-pan
          onMouseEnter={onEnter}
          onMouseLeave={onLeave}
          onPointerDown={(e) => e.stopPropagation()}
        >
          {label && (
            <div className="text-[10px] font-mono text-gray-400 dark:text-gray-500 mb-1">{label}</div>
          )}
          <button
            type="button"
            className="inline-flex items-center gap-1 text-[11px] font-medium text-blue-600 dark:text-blue-400 hover:underline"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setOpen(false);
              onAdd(dayIso);
            }}
          >
            <FilePlus2 className="w-3 h-3" />
            Add content
          </button>
        </div>
      )}
    </div>
  );
}

/** Zoom + slide timeline. Today at 20% left when panDays=0. */
function CalendarVisual({
  elements,
  todayMs,
  zoom,
  onZoomChange,
  panDays,
  onPanDaysChange,
  onSelectElement,
  onOpenDayCluster,
  onAddContentForDay,
  onReschedule,
  onHoverLinkedThoughts,
  highlightedThoughtIds,
  compact = false,
}: {
  elements: ArcElement[];
  todayMs: number;
  zoom: TimelineZoom;
  onZoomChange: (zoom: TimelineZoom) => void;
  /** Continuous day shift of the window (0 = today at 20% left). */
  panDays: number;
  onPanDaysChange: (days: number) => void;
  onSelectElement: (el: ArcElement) => void;
  onOpenDayCluster?: (dayIso: string, items: ArcElement[]) => void;
  onAddContentForDay?: (dayIso: string) => void;
  onReschedule?: (target: {
    kind: 'content';
    id: string;
    scheduledFor: string;
  }) => void;
  /** Detail page: hover content → highlight linked ideas in stage columns. */
  onHoverLinkedThoughts?: (thoughtIds: string[]) => void;
  /** Idea/stage hover → emphasize matching content chips. */
  highlightedThoughtIds?: Set<string>;
  compact?: boolean;
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const rescheduleRef = useRef<{
    kind: 'content';
    id: string;
    startDate: string;
    previewTs: number;
    moved: boolean;
    pointerId: number;
    startClientX: number;
  } | null>(null);
  const panDragRef = useRef<{
    startClientX: number;
    startPanDays: number;
    moved: boolean;
    pointerId: number;
  } | null>(null);
  const [reschedulePreview, setReschedulePreview] = useState<{
    kind: 'content';
    id: string;
    previewTs: number;
  } | null>(null);
  const [hoverClusterKey, setHoverClusterKey] = useState<string | null>(null);
  const [panning, setPanning] = useState(false);
  const suppressClickRef = useRef(false);
  const panAnimRef = useRef<number | null>(null);
  const panDaysRef = useRef(panDays);
  panDaysRef.current = panDays;
  const onHoverLinkedThoughtsRef = useRef(onHoverLinkedThoughts);
  onHoverLinkedThoughtsRef.current = onHoverLinkedThoughts;

  useEffect(() => {
    return () => {
      if (panAnimRef.current != null) cancelAnimationFrame(panAnimRef.current);
      onHoverLinkedThoughtsRef.current?.([]);
    };
  }, []);

  function setClusterHover(clusterKey: string | null, items: ArcElement[]) {
    setHoverClusterKey(clusterKey);
    if (!onHoverLinkedThoughts) return;
    if (!clusterKey) {
      onHoverLinkedThoughts([]);
      return;
    }
    onHoverLinkedThoughts(linkedThoughtIdsFromElements(items));
  }

  const win = useMemo(
    () => timelineViewDomain(todayMs, zoom, panDays),
    [todayMs, zoom, panDays],
  );
  const allDated = useMemo(
    () =>
      datedElements(
        elements.filter((e) => Boolean(e.date)),
        todayMs,
      ).sort((a, b) => a.ts - b.ts),
    [elements, todayMs],
  );
  const dated = useMemo(() => {
    const base = allDated;
    const withPreview = reschedulePreview
      ? base.map((e) => {
          const match =
            reschedulePreview.kind === 'content'
              ? e.site_content_id === reschedulePreview.id
              : e.thought_id === reschedulePreview.id && !e.site_content_id;
          if (!match) return e;
          const date = isoDateLocal(reschedulePreview.previewTs);
          return { ...e, date, ts: reschedulePreview.previewTs };
        })
      : base;
    return withPreview.filter((e) => e.ts >= win.start && e.ts <= win.end);
  }, [allDated, win.start, win.end, reschedulePreview]);
  const offscreen = useMemo(() => {
    const left = allDated.filter((e) => e.ts < win.start);
    const right = allDated.filter((e) => e.ts > win.end);
    return {
      leftCount: left.length,
      rightCount: right.length,
      /** Nearest content just outside the left edge (latest before window). */
      nearestLeft: left.length > 0 ? left[left.length - 1]! : null,
      /** Nearest content just outside the right edge (earliest after window). */
      nearestRight: right.length > 0 ? right[0]! : null,
    };
  }, [allDated, win.start, win.end]);
  const clusters = useMemo(() => clusterTimelineMarkers(dated, win), [dated, win]);
  const lanes = useMemo(() => assignTimelineLanes(clusters), [clusters]);
  const maxLane = useMemo(
    () => lanes.reduce((m, n) => Math.max(m, n), 0) + 1,
    [lanes],
  );
  const viewH = calendarViewHeight(maxLane, compact);
  // Keep axis low so staggered labels have room above (up to ~10 rows).
  const calY = viewH - (compact ? 52 : 64);
  const view = { w: CAL_VIEW_W, h: viewH };

  const ticks = useMemo(() => timelineAxisTicks(win, zoom), [win.start, win.end, zoom]);
  const todayInRange = todayMs >= win.start && todayMs <= win.end;
  const todayX = calendarX(todayMs, win);
  const lastX = dated.length > 0 ? calendarX(dated[dated.length - 1].ts, win) : null;
  const firstX = dated.length > 0 ? calendarX(dated[0].ts, win) : null;

  const usableFracStart = CAL_X0 / CAL_VIEW_W;
  const usableFracEnd = CAL_X1 / CAL_VIEW_W;
  const stepDays = TIMELINE_ZOOM[zoom].stepDays;

  /** Ease panDays to a target (used by earlier/later jump and arrow steps). */
  function animatePanTo(targetDays: number, durationMs = 420) {
    if (panAnimRef.current != null) {
      cancelAnimationFrame(panAnimRef.current);
      panAnimRef.current = null;
    }
    const start = panDaysRef.current;
    const delta = targetDays - start;
    if (Math.abs(delta) < 0.02) {
      onPanDaysChange(targetDays);
      return;
    }
    const t0 = performance.now();
    const step = (now: number) => {
      const t = Math.min(1, (now - t0) / durationMs);
      // ease-in-out cubic — smooth start and settle
      const e = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      onPanDaysChange(start + delta * e);
      if (t < 1) {
        panAnimRef.current = requestAnimationFrame(step);
      } else {
        panAnimRef.current = null;
        onPanDaysChange(targetDays);
      }
    };
    panAnimRef.current = requestAnimationFrame(step);
  }

  /** Pan so target sits at TODAY_FRAC (same anchor as “today” in the default window). */
  function jumpToTimestamp(ts: number) {
    animatePanTo((ts - todayMs) / DAY_MS);
  }

  function cancelPanAnimation() {
    if (panAnimRef.current != null) {
      cancelAnimationFrame(panAnimRef.current);
      panAnimRef.current = null;
    }
  }

  function tsFromClientX(clientX: number): number {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return todayMs;
    const rawFrac = (clientX - rect.left) / rect.width;
    const adjusted = (rawFrac - usableFracStart) / (usableFracEnd - usableFracStart);
    return win.start + Math.min(1, Math.max(0, adjusted)) * (win.end - win.start);
  }

  function handlePointerDown(e: ReactPointerEvent<HTMLDivElement>) {
    if (rescheduleRef.current) return;
    const target = e.target as HTMLElement | null;
    if (target?.closest('button, a, input, select, textarea, [data-no-pan]')) return;
    cancelPanAnimation();
    // Background drag slides the window left/right (continuous).
    panDragRef.current = {
      startClientX: e.clientX,
      startPanDays: panDays,
      moved: false,
      pointerId: e.pointerId,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  }

  function handlePointerMove(e: ReactPointerEvent<HTMLDivElement>) {
    const reschedule = rescheduleRef.current;
    if (reschedule && e.pointerId === reschedule.pointerId) {
      const ts = tsFromClientX(e.clientX);
      const dayIso = isoDateLocal(ts);
      const previewTs = noonLocalFromIso(dayIso);
      if (Math.abs(e.clientX - reschedule.startClientX) > CLICK_VS_DRAG_THRESHOLD_PX) {
        reschedule.moved = true;
      }
      if (dayIso !== reschedule.startDate) reschedule.moved = true;
      reschedule.previewTs = previewTs;
      setReschedulePreview({ kind: reschedule.kind, id: reschedule.id, previewTs });
      return;
    }

    const pan = panDragRef.current;
    if (!pan || e.pointerId !== pan.pointerId) return;
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return;
    const deltaPx = e.clientX - pan.startClientX;
    if (Math.abs(deltaPx) > CLICK_VS_DRAG_THRESHOLD_PX) {
      pan.moved = true;
      setPanning(true);
    }
    if (!pan.moved) return;
    const usableWidthPx = rect.width * (usableFracEnd - usableFracStart);
    const spanMs = win.end - win.start;
    const deltaTime = (deltaPx / usableWidthPx) * spanMs;
    // Drag right → look earlier (negative panDays). Continuous, no step snap.
    onPanDaysChange(pan.startPanDays - deltaTime / DAY_MS);
  }

  function handlePointerUp(e: ReactPointerEvent<HTMLDivElement>) {
    const reschedule = rescheduleRef.current;
    if (reschedule && e.pointerId === reschedule.pointerId) {
      const nextDate = isoDateLocal(reschedule.previewTs);
      const moved = reschedule.moved && nextDate !== reschedule.startDate;
      rescheduleRef.current = null;
      setReschedulePreview(null);
      panDragRef.current = null;
      setPanning(false);
      if (moved) {
        suppressClickRef.current = true;
        if (onReschedule) {
          onReschedule({ kind: reschedule.kind, id: reschedule.id, scheduledFor: nextDate });
        }
      }
      return;
    }

    const pan = panDragRef.current;
    panDragRef.current = null;
    setPanning(false);
    if (pan?.moved) suppressClickRef.current = true;
  }

  function beginFlowReschedule(el: ArcElement, e: ReactPointerEvent) {
    if (!isReschedulable(el) || !onReschedule) return;
    const kind = 'content' as const;
    const id = el.site_content_id;
    if (!id) return;
    e.stopPropagation();
    e.preventDefault();
    panDragRef.current = null;
    setPanning(false);
    const startDate = el.date?.slice(0, 10) ?? isoDateLocal(Date.now());
    const previewTs = noonLocalFromIso(startDate);
    rescheduleRef.current = {
      kind,
      id,
      startDate,
      previewTs,
      moved: false,
      pointerId: e.pointerId,
      startClientX: e.clientX,
    };
    setReschedulePreview({ kind, id, previewTs });
    trackRef.current?.setPointerCapture(e.pointerId);
  }

  const previewDateLabel = reschedulePreview
    ? formatShortDate(isoDateLocal(reschedulePreview.previewTs))
    : null;

  const zoomBtn =
    'px-2 py-1 text-[11px] rounded-md transition-colors font-medium';
  const zoomActive = 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm';
  const zoomIdle = 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200';

  return (
    <div className={compact ? 'mt-0' : 'mt-3'}>
      <div className={`flex items-center justify-between gap-3 flex-wrap ${compact ? 'mb-1' : 'mb-1.5'}`}>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex items-center gap-0.5 bg-gray-100 dark:bg-gray-900 rounded-lg p-0.5 flex-wrap">
            {TIMELINE_ZOOM_ORDER.map((z) => (
              <button
                key={z}
                type="button"
                className={`${zoomBtn} ${zoom === z ? zoomActive : zoomIdle}`}
                onClick={() => {
                  cancelPanAnimation();
                  onZoomChange(z);
                  onPanDaysChange(0);
                }}
              >
                {TIMELINE_ZOOM[z].label}
              </button>
            ))}
          </div>
          <span className="text-[11px] uppercase tracking-wide font-mono text-gray-400 dark:text-gray-500">
            {compact
              ? 'drag to slide · handle to reschedule'
              : 'drag axis to slide · handle to reschedule'}
            {previewDateLabel && ` · drop on ${previewDateLabel}`}
          </span>
          {!compact && (
          <span className="inline-flex items-center gap-2 text-[10px] font-mono text-gray-500 dark:text-gray-400">
            {STAGE_ORDER.map((s) => (
              <span key={s} className="inline-flex items-center gap-1">
                <span
                  className={`inline-block w-2.5 h-2.5 rounded-sm border ${STAGE_CHIP[s].bg} ${STAGE_CHIP[s].border}`}
                />
                {STAGE_LABEL[s]}
              </span>
            ))}
          </span>
          )}
        </div>
        <div className="flex items-center gap-1.5 flex-wrap justify-end">
          {offscreen.leftCount > 0 && offscreen.nearestLeft && (
            <button
              type="button"
              onClick={() => jumpToTimestamp(offscreen.nearestLeft!.ts)}
              className="inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded-md border border-amber-300/80 dark:border-amber-700 text-amber-800 dark:text-amber-300 bg-amber-50/80 dark:bg-amber-950/30 hover:bg-amber-100 dark:hover:bg-amber-950/50"
              title={`Jump to nearest earlier content (${formatShortDate(offscreen.nearestLeft.date ?? '')})`}
            >
              <ChevronLeft className="w-3.5 h-3.5" />
              {offscreen.leftCount} earlier
            </button>
          )}
          <button
            type="button"
            onClick={() => animatePanTo(panDaysRef.current - stepDays)}
            className="inline-flex items-center justify-center w-7 h-7 rounded-md border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            title="Slide earlier"
            aria-label="Slide earlier"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="min-w-[10.5rem] text-center text-[11px] font-mono text-gray-600 dark:text-gray-300">
            {formatWindowRange(win, zoom)}
          </span>
          <button
            type="button"
            onClick={() => animatePanTo(panDaysRef.current + stepDays)}
            className="inline-flex items-center justify-center w-7 h-7 rounded-md border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            title="Slide later"
            aria-label="Slide later"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          {offscreen.rightCount > 0 && offscreen.nearestRight && (
            <button
              type="button"
              onClick={() => jumpToTimestamp(offscreen.nearestRight!.ts)}
              className="inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded-md border border-amber-300/80 dark:border-amber-700 text-amber-800 dark:text-amber-300 bg-amber-50/80 dark:bg-amber-950/30 hover:bg-amber-100 dark:hover:bg-amber-950/50"
              title={`Jump to nearest later content (${formatShortDate(offscreen.nearestRight.date ?? '')})`}
            >
              {offscreen.rightCount} later
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          )}
          {Math.abs(panDays) > 0.01 && (
            <button
              type="button"
              onClick={() => animatePanTo(0)}
              className="ml-1 text-[11px] text-blue-600 dark:text-blue-400 hover:underline px-1"
            >
              Today
            </button>
          )}
        </div>
      </div>

      <div
        ref={trackRef}
        className={`relative w-full select-none touch-none ${
          reschedulePreview || panning ? 'cursor-grabbing' : 'cursor-grab'
        }`}
        style={{ paddingBottom: `${(viewH / CAL_VIEW_W) * 100}%` }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        {offscreen.leftCount > 0 && offscreen.nearestLeft && (
          <button
            type="button"
            data-no-pan
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              jumpToTimestamp(offscreen.nearestLeft!.ts);
            }}
            className="absolute left-1 top-1/2 -translate-y-1/2 z-30 inline-flex items-center gap-0.5 px-1.5 py-1 rounded-full text-[10px] font-mono font-semibold bg-amber-500 text-white shadow-md hover:bg-amber-600"
            title="Jump to next earlier content"
          >
            <ChevronLeft className="w-3 h-3" />
            {offscreen.leftCount}
          </button>
        )}
        {offscreen.rightCount > 0 && offscreen.nearestRight && (
          <button
            type="button"
            data-no-pan
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              jumpToTimestamp(offscreen.nearestRight!.ts);
            }}
            className="absolute right-1 top-1/2 -translate-y-1/2 z-30 inline-flex items-center gap-0.5 px-1.5 py-1 rounded-full text-[10px] font-mono font-semibold bg-amber-500 text-white shadow-md hover:bg-amber-600"
            title="Jump to next later content"
          >
            {offscreen.rightCount}
            <ChevronRight className="w-3 h-3" />
          </button>
        )}
        <svg
          className="absolute inset-0 w-full h-full"
          viewBox={`0 0 ${CAL_VIEW_W} ${viewH}`}
          preserveAspectRatio="xMidYMid meet"
        >
          {/* Month / week dividing lines through the chart when that zoom needs them. */}
          {ticks
            .filter((tk) => showDividerLine(zoom, tk.rank))
            .map((tk) => {
              const x = calendarX(tk.ts, win);
              const isWeek = tk.rank === 'week';
              return (
                <line
                  key={`div-${tk.dayIso}-${tk.rank}`}
                  x1={x}
                  x2={x}
                  y1={12}
                  y2={calY + 14}
                  strokeWidth={isWeek ? 1 : 1.25}
                  strokeDasharray={isWeek ? '2 5' : undefined}
                  className={
                    isWeek
                      ? 'stroke-gray-200/80 dark:stroke-gray-700/80'
                      : 'stroke-gray-300/90 dark:stroke-gray-600/90'
                  }
                />
              );
            })}

          {ticks.map((tk) => {
            const x = calendarX(tk.ts, win);
            const { above, below } = tickMarkExtents(tk.rank);
            const stroke =
              tk.rank === 'major'
                ? 'stroke-gray-300 dark:stroke-gray-500'
                : tk.rank === 'month'
                  ? 'stroke-gray-300 dark:stroke-gray-600'
                  : 'stroke-gray-200 dark:stroke-gray-700';
            return (
              <line
                key={`tick-${tk.dayIso}-${tk.rank}`}
                x1={x}
                x2={x}
                y1={calY - above}
                y2={calY + below}
                strokeWidth={tk.rank === 'major' ? 1.25 : 1}
                className={stroke}
              />
            );
          })}

          {/* Continuous time axis across the full window. */}
          <line
            x1={CAL_X0}
            x2={CAL_X1}
            y1={calY}
            y2={calY}
            strokeWidth={1.5}
            className="stroke-gray-300 dark:stroke-gray-600"
          />

          {firstX !== null && lastX !== null && (
            <line
              x1={firstX}
              x2={lastX}
              y1={calY}
              y2={calY}
              strokeWidth={2.5}
              className="stroke-blue-600 dark:stroke-blue-400"
            />
          )}

          {/* Stem from each content chip down to its time on the axis. */}
          {clusters.map((cluster, ci) => {
            const lane = lanes[ci] ?? 0;
            const anchorY = calY - TIMELINE_AXIS_GAP - lane * TIMELINE_LANE_STEP;
            return (
              <line
                key={`time-stem-${cluster.ts}-${ci}`}
                x1={cluster.x}
                x2={cluster.x}
                y1={anchorY}
                y2={calY}
                strokeWidth={1}
                className="stroke-blue-400/50 dark:stroke-blue-500/40"
              />
            );
          })}

          {todayInRange && (
            <line
              x1={todayX}
              x2={todayX}
              y1={12}
              y2={calY + 18}
              strokeWidth={1.5}
              className="stroke-rose-500 dark:stroke-rose-400"
            />
          )}

          {clusters.map((cluster, ci) => {
            const stackN = cluster.items.length;
            if (stackN > 1) {
              return (
                <circle
                  key={`dot-${cluster.ts}-${ci}`}
                  cx={cluster.x}
                  cy={calY}
                  r={3}
                  className="fill-blue-600 dark:fill-blue-400"
                />
              );
            }
            const m = cluster.items[0]!;
            return (
              <circle
                key={`dot-${cluster.ts}-${ci}`}
                cx={cluster.x}
                cy={calY}
                r={m.dateKind === 'published' ? 5 : 4}
                className={
                  m.dateKind === 'published'
                    ? 'fill-blue-600 dark:fill-blue-400'
                    : 'fill-white dark:fill-gray-800 stroke-blue-500 dark:stroke-blue-400'
                }
                strokeWidth={m.dateKind === 'published' ? 0 : 1.5}
              />
            );
          })}
        </svg>

        {ticks
          .filter((tk) => tk.label)
          .map((tk) => {
            const x = calendarX(tk.ts, win);
            const isMonthSub = tk.rank === 'month';
            return (
              <div
                key={`tick-label-${tk.dayIso}-${tk.rank}`}
                className="absolute -translate-x-1/2"
                style={pctOf(view, { x, y: calY + (isMonthSub ? 16 : 18) })}
                data-no-pan
              >
                <DateAddHover
                  dayIso={tk.dayIso}
                  label={tk.label}
                  onAdd={onAddContentForDay}
                >
                  <button
                    type="button"
                    className={`font-mono hover:text-blue-600 dark:hover:text-blue-400 ${
                      isMonthSub
                        ? 'text-[9px] text-gray-400 dark:text-gray-500'
                        : 'text-[10px] text-gray-400 dark:text-gray-500'
                    }`}
                    onPointerDown={(e) => e.stopPropagation()}
                  >
                    {tk.label}
                  </button>
                </DateAddHover>
              </div>
            );
          })}

        {todayInRange && (
          <div
            className="absolute -translate-x-1/2 text-[10px] font-mono text-rose-500 dark:text-rose-400 whitespace-nowrap"
            style={pctOf(view, { x: todayX, y: calY + 34 })}
          >
            today
          </div>
        )}

        {dated.length === 0 && (
          <div
            className="absolute -translate-x-1/2 -translate-y-1/2 text-xs font-mono text-gray-400 dark:text-gray-500 whitespace-nowrap"
            style={pctOf(view, { x: CAL_X0 + 40, y: calY })}
          >
            {elements.length === 0 ? 'no publish dates yet' : 'nothing in this window'}
          </div>
        )}

        {clusters.map((cluster, ci) => {
          const stackN = cluster.items.length;
          const dayIso = cluster.items[0]?.date?.slice(0, 10) ?? '';
          const dateLabel = dayIso ? formatShortDate(dayIso) : '';
          const lane = lanes[ci] ?? 0;
          const clusterKey = `${cluster.ts}-${ci}`;
          const hovered = hoverClusterKey === clusterKey;
          const linkedLit =
            !hovered &&
            Boolean(highlightedThoughtIds?.size) &&
            linkedThoughtIdsFromElements(cluster.items).some((id) =>
              highlightedThoughtIds!.has(id),
            );
          const crowded = timelineLabelCrowded(clusters, ci);
          const anchorY = calY - TIMELINE_AXIS_GAP - lane * TIMELINE_LANE_STEP;
          // Hover / linked-highlight wins stacking — solid chips sit on top when emphasized.
          const z = hovered ? 80 : linkedLit ? 70 : 10 + lane;
          const emphasize = hovered || linkedLit;

          if (stackN > 1) {
            const stackStage = stageOfElement(cluster.items[0]!);
            const stackChip = STAGE_CHIP[stackStage];
            return (
              <div
                key={`cluster-label-${clusterKey}`}
                className="absolute -translate-x-1/2 -translate-y-full text-center"
                style={{ ...pctOf(view, { x: cluster.x, y: anchorY }), zIndex: z }}
                data-no-pan
                onMouseEnter={() => setClusterHover(clusterKey, cluster.items)}
                onMouseLeave={() => {
                  setHoverClusterKey((k) => (k === clusterKey ? null : k));
                  onHoverLinkedThoughts?.([]);
                }}
                onPointerDown={(e) => e.stopPropagation()}
              >
                <div
                  className={`px-1.5 py-1 rounded-md border ${
                    linkedLit
                      ? 'shadow-[0_8px_28px_rgba(37,99,235,0.5)] dark:shadow-[0_8px_28px_rgba(96,165,250,0.45)]'
                      : emphasize
                        ? 'shadow-md'
                        : 'shadow-sm'
                  } ${stackChip.bg} ${
                    crowded || emphasize ? stackChip.borderHover : stackChip.border
                  }`}
                >
                  {dateLabel && (
                    <DateAddHover
                      dayIso={dayIso}
                      label={dateLabel}
                      onAdd={onAddContentForDay}
                    >
                      <div className={`text-[11px] font-mono leading-tight mb-1 cursor-default ${stackChip.meta}`}>
                        {dateLabel}
                      </div>
                    </DateAddHover>
                  )}
                  <button
                    type="button"
                    title={`${stackN} content on this day — open`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenDayCluster?.(dayIso, cluster.items);
                    }}
                    className="mx-auto flex items-center justify-center w-7 h-7 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-[12px] font-mono font-semibold shadow-sm"
                  >
                    {stackN}
                  </button>
                </div>
              </div>
            );
          }

          const m = cluster.items[0]!;
          const movable = isReschedulable(m);
          const locked = m.dateKind === 'published';
          const stage = stageOfElement(m);
          const chip = STAGE_CHIP[stage];
          return (
            <div
              key={`cluster-label-${clusterKey}`}
              className="absolute -translate-x-1/2 -translate-y-full text-center min-w-[148px] max-w-[220px] w-max"
              style={{ ...pctOf(view, { x: cluster.x, y: anchorY }), zIndex: z }}
              data-no-pan
              onMouseEnter={() => setClusterHover(clusterKey, cluster.items)}
              onMouseLeave={() => {
                setHoverClusterKey((k) => (k === clusterKey ? null : k));
                onHoverLinkedThoughts?.([]);
              }}
              onPointerDown={(e) => e.stopPropagation()}
            >
              <div
                className={`relative min-w-[148px] px-2.5 pt-1.5 pb-1.5 rounded-md border text-left ${
                  linkedLit
                    ? 'shadow-[0_8px_28px_rgba(37,99,235,0.5)] dark:shadow-[0_8px_28px_rgba(96,165,250,0.45)]'
                    : emphasize
                      ? 'shadow-md'
                      : 'shadow-sm'
                } ${chip.bg} ${crowded || emphasize ? chip.borderHover : chip.border}`}
              >
                {movable && (
                  <button
                    type="button"
                    title="Drag to reschedule"
                    aria-label="Drag to reschedule"
                    className={`absolute -top-1.5 -left-1.5 z-10 p-0.5 rounded border border-gray-300 dark:border-gray-500 bg-white dark:bg-gray-800 text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 cursor-grab active:cursor-grabbing shadow-sm transition-opacity ${
                      hovered ? 'opacity-100' : 'opacity-0 pointer-events-none'
                    }`}
                    onPointerDown={(e) => beginFlowReschedule(m, e)}
                  >
                    <GripVertical className="w-3.5 h-3.5" />
                  </button>
                )}
                {dateLabel && (
                  <DateAddHover dayIso={dayIso} label={dateLabel} onAdd={onAddContentForDay}>
                    <div className={`text-[11px] font-mono leading-tight mb-0.5 cursor-default ${chip.meta}`}>
                      {dateLabel}
                    </div>
                  </DateAddHover>
                )}
                <button
                  type="button"
                  title={
                    locked
                      ? `${m.label} · ${STAGE_LABEL[stage]} · published`
                      : `${m.label} · ${STAGE_LABEL[stage]}`
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    if (suppressClickRef.current) {
                      suppressClickRef.current = false;
                      return;
                    }
                    onSelectElement(m);
                  }}
                  className={`w-full min-w-[120px] text-left text-[12.5px] font-medium leading-snug whitespace-normal break-words line-clamp-3 ${
                    compact ? 'text-[11.5px]' : ''
                  } ${chip.title} ${locked ? 'cursor-default' : 'hover:underline'}`}
                >
                  {m.label}
                </button>
                <div className={`text-[10px] font-mono leading-tight ${chip.meta}`}>
                  {STAGE_LABEL[stage].toLowerCase()}
                  {movable ? ' · draft' : locked ? ' · published' : ''}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Collapsed multi-arc row: idea maturity chips + content sub-dots (idea mode) or sparkline. */
function MiniIndicator({ elements, todayMs, mode, domain }: { elements: ArcElement[]; todayMs: number; mode: ViewMode; domain: Domain }) {
  if (mode === 'idea') {
    const ideas = elements.filter((e) => e.lane === 'thought');
    if (ideas.length === 0) {
      return <span className="text-[11px] font-mono text-gray-300 dark:text-gray-600 shrink-0">no ideas</span>;
    }
    return (
      <div className="flex items-center gap-2 shrink-0 max-w-[220px] overflow-hidden">
        {ideas.slice(0, 8).map((el) => (
          <span
            key={el.thought_id ?? el.label}
            className="inline-flex items-center gap-0.5"
            title={`${el.label} · ${publishStatusLabel(el.publishStatus)}`}
          >
            <span
              className={
                el.publishStatus === 'published'
                  ? 'w-2 h-2 rounded-full bg-blue-600 dark:bg-blue-400'
                  : el.publishStatus === 'planned'
                    ? 'w-2 h-2 rounded-full border-2 border-blue-400 dark:border-blue-500 bg-transparent'
                    : 'w-2 h-2 rounded-full border border-dashed border-gray-300 dark:border-gray-600 bg-transparent'
              }
            />
            <ContentSubDots pubs={el.publications ?? []} />
          </span>
        ))}
        {ideas.length > 8 && (
          <span className="text-[10px] font-mono text-gray-400">+{ideas.length - 8}</span>
        )}
      </div>
    );
  }
  const dated = datedElements(
    elements.filter((e) => Boolean(e.date)),
    todayMs,
  );
  return (
    <div className="relative w-32 h-1.5 shrink-0">
      <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-px bg-gray-200 dark:bg-gray-700" />
      {dated.map((m, i) => (
        <div
          key={i}
          className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-blue-600 dark:bg-blue-400"
          style={{ left: `${((calendarX(m.ts, domain) - CAL_X0) / (CAL_X1 - CAL_X0)) * 100}%` }}
        />
      ))}
    </div>
  );
}

function ArcLane({
  node,
  elements,
  timelineElements,
  todayMs,
  mode,
  timelineZoom,
  onTimelineZoomChange,
  panDays,
  onPanDaysChange,
  expanded,
  primary,
  onToggle,
  onSelectElement,
  onOpenDayCluster,
  onAddContentForDay,
  onMoveStage,
  onReschedule,
  onAddToStage,
  onAddContentForIdea,
  onUpdateMeta,
  sectionRef,
  coverageHint,
  layout = 'list',
}: {
  node: ArcNodeRow;
  elements: ArcElement[];
  timelineElements?: ArcElement[];
  todayMs: number;
  mode: ViewMode;
  timelineZoom: TimelineZoom;
  onTimelineZoomChange: (zoom: TimelineZoom) => void;
  panDays: number;
  onPanDaysChange: (days: number) => void;
  expanded: boolean;
  primary: boolean;
  onToggle: () => void;
  onSelectElement: (el: ArcElement) => void;
  onOpenDayCluster?: (dayIso: string, items: ArcElement[]) => void;
  onAddContentForDay?: (dayIso: string) => void;
  onMoveStage?: (thoughtId: string, stage: Stage) => void;
  onReschedule?: (target: {
    kind: 'content';
    id: string;
    scheduledFor: string;
  }) => void;
  onAddToStage?: (stage: Stage) => void;
  onAddContentForIdea?: (thoughtId: string) => void;
  onUpdateMeta?: (patch: { label?: string; role_description?: string | null }) => void | Promise<void>;
  sectionRef?: (el: HTMLElement | null) => void;
  coverageHint?: 'unconfirmed' | 'holding' | 'framed_unshipped';
  layout?: 'list' | 'detail';
}) {
  const annotations = node.annotations ?? {};
  const stored = annotations.maturity;
  const meta = maturityMeta(stored);
  const hintMeta = coverageHint && !stored ? maturityMeta(coverageHint) : null;
  const pill = hintMeta ?? meta;
  const isDetail = layout === 'detail';
  const timelineEls = timelineElements ?? elements;
  const sparkDomain = useMemo(
    () => timelineViewDomain(todayMs, 'months', 0),
    [todayMs],
  );
  const [editingLabel, setEditingLabel] = useState(false);
  const [editingDesc, setEditingDesc] = useState(false);
  const [draftLabel, setDraftLabel] = useState(node.label);
  const [draftDesc, setDraftDesc] = useState(node.role_description ?? '');
  const [savingMeta, setSavingMeta] = useState<'label' | 'desc' | null>(null);
  const [highlightThoughtIds, setHighlightThoughtIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [stageContentVisible, setStageContentVisible] = useState<StageContentVisible>(() =>
    readStageContentVisible(node.slug),
  );
  const labelInputRef = useRef<HTMLInputElement>(null);
  const descInputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setStageContentVisible(readStageContentVisible(node.slug));
  }, [node.slug]);

  useEffect(() => {
    // Only auto-scroll for a single idea (timeline → idea), not whole-stage highlights.
    if (highlightThoughtIds.size !== 1) return;
    const first = [...highlightThoughtIds][0];
    if (!first) return;
    const el = document.querySelector(
      `[data-thought-id="${CSS.escape(first)}"]`,
    );
    el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [highlightThoughtIds]);

  function toggleStageContent(stage: Stage) {
    setStageContentVisible((prev) => {
      const next = { ...prev, [stage]: !prev[stage] };
      writeStageContentVisible(node.slug, next);
      return next;
    });
  }

  const visibleTimelineEls = useMemo(() => {
    if (!isDetail) return timelineEls;
    const thoughtStageById = new Map<string, Stage>();
    for (const el of elements) {
      if (el.thought_id) thoughtStageById.set(el.thought_id, stageOfElement(el));
    }
    return timelineEls.filter((el) => {
      const tid =
        el.source_thought_id != null
          ? String(el.source_thought_id)
          : el.thought_id;
      const stage = (tid && thoughtStageById.get(tid)) || stageOfElement(el);
      return stageContentVisible[stage] !== false;
    });
  }, [isDetail, timelineEls, elements, stageContentVisible]);

  const hiddenStageCount = STAGE_ORDER.filter((s) => !stageContentVisible[s]).length;

  useEffect(() => {
    if (!editingLabel) setDraftLabel(node.label);
    if (!editingDesc) setDraftDesc(node.role_description ?? '');
  }, [node.id, node.label, node.role_description, editingLabel, editingDesc]);

  useEffect(() => {
    if (editingLabel) labelInputRef.current?.focus();
  }, [editingLabel]);

  useEffect(() => {
    if (editingDesc) descInputRef.current?.focus();
  }, [editingDesc]);

  function startLabelEdit() {
    if (!onUpdateMeta || savingMeta) return;
    setDraftLabel(node.label);
    setEditingLabel(true);
  }

  function cancelLabelEdit() {
    if (savingMeta === 'label') return;
    setDraftLabel(node.label);
    setEditingLabel(false);
  }

  async function commitLabel() {
    if (!onUpdateMeta || savingMeta) return;
    const next = draftLabel.trim();
    if (!next) {
      toast.error('Arc name is required');
      return;
    }
    if (next === node.label) {
      setEditingLabel(false);
      return;
    }
    setSavingMeta('label');
    try {
      await onUpdateMeta({ label: next });
      setEditingLabel(false);
    } finally {
      setSavingMeta(null);
    }
  }

  function startDescEdit() {
    if (!onUpdateMeta || savingMeta) return;
    setDraftDesc(node.role_description ?? '');
    setEditingDesc(true);
  }

  function cancelDescEdit() {
    if (savingMeta === 'desc') return;
    setDraftDesc(node.role_description ?? '');
    setEditingDesc(false);
  }

  async function commitDesc() {
    if (!onUpdateMeta || savingMeta) return;
    const next = draftDesc.trim();
    const prev = (node.role_description ?? '').trim();
    if (next === prev) {
      setEditingDesc(false);
      return;
    }
    setSavingMeta('desc');
    try {
      await onUpdateMeta({ role_description: next || null });
      setEditingDesc(false);
    } finally {
      setSavingMeta(null);
    }
  }

  return (
    <section
      ref={sectionRef}
      data-arc-slug={node.slug}
      className={
        isDetail
          ? 'min-h-[70vh]'
          : `bg-white dark:bg-gray-800 border rounded-lg shadow-sm ${
              primary
                ? 'border-blue-300 dark:border-blue-600 ring-1 ring-blue-200/80 dark:ring-blue-900/50'
                : 'border-gray-200 dark:border-gray-700'
            }`
      }
    >
      {!isDetail ? (
        <div className="w-full flex items-center gap-3 px-4 sm:px-6 py-3">
          <button
            type="button"
            onClick={onToggle}
            className="flex items-center gap-2 text-left min-w-0 flex-1"
          >
            {expanded ? (
              <ChevronDown className="w-4 h-4 text-gray-400 dark:text-gray-500 shrink-0" />
            ) : (
              <ChevronRight className="w-4 h-4 text-gray-400 dark:text-gray-500 shrink-0" />
            )}
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white truncate">
              {node.label}
            </h2>
            <span
              className={`shrink-0 text-[10px] uppercase tracking-wide font-mono px-2 py-0.5 rounded ${pill.pillClass}`}
              title={
                stored
                  ? `Maturity: ${meta.label}`
                  : coverageHint
                    ? `Suggested from coverage: ${pill.label}`
                    : undefined
              }
            >
              {pill.label}
              {!stored && coverageHint ? ' · auto' : ''}
            </span>
            {!expanded && (
              <>
                <span className="hidden sm:block text-sm text-gray-500 dark:text-gray-400 truncate flex-1 min-w-0">
                  {node.role_description ?? 'No thesis yet.'}
                </span>
                <MiniIndicator elements={elements} todayMs={todayMs} mode={mode} domain={sparkDomain} />
              </>
            )}
          </button>
          <Link
            to={`/narrative-arcs/${node.slug}`}
            className="shrink-0 text-[12px] text-blue-600 dark:text-blue-400 hover:underline px-2"
            onClick={(e) => e.stopPropagation()}
          >
            Open canvas
          </Link>
        </div>
      ) : (
        <div className="px-0 pt-0 pb-0">
          <div className="min-w-0 max-w-[70ch] flex flex-wrap items-center gap-x-2.5 gap-y-1">
            {editingLabel ? (
              <div className="flex flex-wrap items-center gap-2 w-full">
                <input
                  ref={labelInputRef}
                  type="text"
                  value={draftLabel}
                  onChange={(e) => setDraftLabel(e.target.value)}
                  disabled={savingMeta === 'label'}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      void commitLabel();
                    }
                    if (e.key === 'Escape') {
                      e.preventDefault();
                      cancelLabelEdit();
                    }
                  }}
                  aria-label="Edit arc name"
                  className="flex-1 min-w-[14rem] px-2 py-1 text-xl sm:text-2xl font-semibold border border-blue-300 dark:border-blue-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white tracking-tight focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                />
                <button
                  type="button"
                  onClick={() => void commitLabel()}
                  disabled={savingMeta === 'label'}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {savingMeta === 'label' ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Check className="w-3.5 h-3.5" />
                  )}
                  Save
                </button>
                <button
                  type="button"
                  onClick={cancelLabelEdit}
                  disabled={savingMeta === 'label'}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                >
                  <XCircle className="w-3.5 h-3.5" />
                  Cancel
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={startLabelEdit}
                title="Click to edit name"
                className="group inline-flex max-w-full items-center gap-2 rounded-md -mx-1 px-1 py-0 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
              >
                <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white tracking-tight">
                  {node.label}
                </h1>
                <Pencil className="w-3.5 h-3.5 shrink-0 text-gray-400 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity" />
              </button>
            )}
            <span
              className={`shrink-0 text-[10px] uppercase tracking-wide font-mono px-2 py-0.5 rounded ${pill.pillClass}`}
            >
              {pill.label}
              {!stored && coverageHint ? ' · auto' : ''}
            </span>
          </div>
        </div>
      )}

      {(expanded || isDetail) && (
        <div className={`px-4 sm:px-6 pb-6 ${isDetail ? 'px-0 sm:px-0 pb-4' : ''}`}>
          {isDetail ? (
            editingDesc ? (
              <div className="max-w-[70ch] mt-1 mb-2 space-y-2">
                <textarea
                  ref={descInputRef}
                  value={draftDesc}
                  onChange={(e) => setDraftDesc(e.target.value)}
                  disabled={savingMeta === 'desc'}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      e.preventDefault();
                      cancelDescEdit();
                    }
                  }}
                  aria-label="Edit arc description"
                  rows={Math.min(6, Math.max(2, draftDesc.split('\n').length + 1))}
                  placeholder="Add a short thesis / description…"
                  className="w-full px-2 py-1 text-[13px] sm:text-[14px] leading-snug italic border border-blue-300 dark:border-blue-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 resize-y"
                />
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => void commitDesc()}
                    disabled={savingMeta === 'desc'}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                  >
                    {savingMeta === 'desc' ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Check className="w-3.5 h-3.5" />
                    )}
                    Save
                  </button>
                  <button
                    type="button"
                    onClick={cancelDescEdit}
                    disabled={savingMeta === 'desc'}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                  >
                    <XCircle className="w-3.5 h-3.5" />
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={startDescEdit}
                title="Click to edit description"
                className="group mt-0.5 mb-2 max-w-[70ch] w-full text-left rounded-md -mx-1 px-1 py-0.5 hover:bg-gray-50 dark:hover:bg-gray-700/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
              >
                <span className="inline-flex items-start gap-2 w-full">
                  {node.role_description?.trim() ? (
                    <span className="italic text-[13px] sm:text-[14px] leading-snug text-gray-700 dark:text-gray-200">
                      {node.role_description}
                    </span>
                  ) : (
                    <span className="text-[13px] text-gray-400 dark:text-gray-500 italic">
                      Add a short thesis / description…
                    </span>
                  )}
                  <Pencil className="w-3.5 h-3.5 shrink-0 mt-0.5 text-gray-400 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity" />
                </span>
              </button>
            )
          ) : node.role_description ? (
            <p className="italic text-[14px] sm:text-[15px] leading-relaxed text-gray-700 dark:text-gray-200 max-w-[70ch] mt-0 mb-3">
              {node.role_description}
            </p>
          ) : (
            <p className="text-[14px] text-gray-400 dark:text-gray-500 mt-0 mb-3">No thesis yet.</p>
          )}

          {isDetail ? (
            <div className="space-y-3">
              <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/60 px-3 sm:px-4 py-2">
                {hiddenStageCount > 0 ? (
                  <div className="text-[10px] uppercase tracking-wider font-mono text-amber-700 dark:text-amber-400 mb-1">
                    {hiddenStageCount} stage{hiddenStageCount === 1 ? '' : 's'} content hidden
                  </div>
                ) : null}
                <CalendarVisual
                  elements={visibleTimelineEls}
                  todayMs={todayMs}
                  zoom={timelineZoom}
                  onZoomChange={onTimelineZoomChange}
                  panDays={panDays}
                  onPanDaysChange={onPanDaysChange}
                  onSelectElement={onSelectElement}
                  onOpenDayCluster={onOpenDayCluster}
                  onAddContentForDay={onAddContentForDay}
                  onReschedule={onReschedule}
                  onHoverLinkedThoughts={(ids) =>
                    setHighlightThoughtIds(new Set(ids))
                  }
                  highlightedThoughtIds={highlightThoughtIds}
                  compact
                />
              </div>
              <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/40 px-3 sm:px-4 py-2.5">
                <div className="text-[10px] uppercase tracking-wider font-mono text-gray-400 dark:text-gray-500 mb-0.5">
                  Ideas by stage
                  <span className="ml-1.5 normal-case tracking-normal text-gray-400 dark:text-gray-500">
                    · eye toggles timeline
                  </span>
                  {highlightThoughtIds.size > 0 ? (
                    <span className="ml-1.5 normal-case tracking-normal text-blue-600 dark:text-blue-400">
                      · linked
                    </span>
                  ) : null}
                </div>
                <IdeaVisual
                  elements={elements}
                  onSelectElement={onSelectElement}
                  onMoveStage={onMoveStage}
                  onAddToStage={onAddToStage}
                  onAddContentForIdea={onAddContentForIdea}
                  highlightThoughtIds={highlightThoughtIds}
                  onHighlightThoughts={(ids) =>
                    setHighlightThoughtIds(new Set(ids))
                  }
                  stageContentVisible={stageContentVisible}
                  onToggleStageContent={toggleStageContent}
                  showCurve={false}
                />
              </div>
            </div>
          ) : (
            <div>
              {mode === 'idea' ? (
                <IdeaVisual
                  elements={elements}
                  onSelectElement={onSelectElement}
                  onMoveStage={onMoveStage}
                  onAddToStage={onAddToStage}
                />
              ) : (
                <CalendarVisual
                  elements={timelineEls}
                  todayMs={todayMs}
                  zoom={timelineZoom}
                  onZoomChange={onTimelineZoomChange}
                  panDays={panDays}
                  onPanDaysChange={onPanDaysChange}
                  onSelectElement={onSelectElement}
                  onOpenDayCluster={onOpenDayCluster}
                  onAddContentForDay={onAddContentForDay}
                  onReschedule={onReschedule}
                />
              )}
            </div>
          )}

          {node.usage_notes && (
            <div className="flex items-baseline gap-2.5 pt-3.5 mt-3 border-t border-gray-200 dark:border-gray-700">
              <span className="text-[10px] uppercase tracking-wider font-mono text-gray-400 dark:text-gray-500 shrink-0">
                Gap
              </span>
              <span className="text-[13px] leading-relaxed text-gray-600 dark:text-gray-400">
                {node.usage_notes}
              </span>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function ModeToggle({ mode, onChange }: { mode: ViewMode; onChange: (m: ViewMode) => void }) {
  const base = 'px-3 py-1 text-sm rounded-md transition-colors';
  const active = 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm';
  const inactive = 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200';
  return (
    <div className="inline-flex items-center gap-1 bg-gray-100 dark:bg-gray-900 rounded-lg p-1">
      <button type="button" className={`${base} ${mode === 'idea' ? active : inactive}`} onClick={() => onChange('idea')}>
        Idea arc
      </button>
      <button type="button" className={`${base} ${mode === 'calendar' ? active : inactive}`} onClick={() => onChange('calendar')}>
        Timeline
      </button>
    </div>
  );
}

type EditableThought = {
  id: string;
  title: string;
  seed_idea: string;
  reference: string;
  status: string;
  final_copy: string;
  arc_slug: string | null;
  arc_stage: string | null;
  arc_placement: string | null;
  arc_fit_note: string | null;
  first_published_at: string | null;
  created_at: string;
  updated_at: string;
};

type NarrativeArcsViewProps = {
  /** When set, show only this arc as a full canvas detail page. */
  focusSlug?: string;
};

export default function NarrativeArcsView({ focusSlug }: NarrativeArcsViewProps = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [nodes, setNodes] = useState<ArcNodeRow[]>([]);
  const [thoughts, setThoughts] = useState<ThoughtArcRow[]>([]);
  const [siteRows, setSiteRows] = useState<SiteContentArcRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [live, setLive] = useState(false);
  const [mode, setMode] = useState<ViewMode>('idea');
  const isDetail = Boolean(focusSlug?.trim());
  const [expanded, setExpanded] = useState<Set<string>>(() =>
    focusSlug?.trim() ? new Set([focusSlug.trim()]) : readStoredExpanded(),
  );
  const [primarySlug, setPrimarySlug] = useState<string | null>(() => {
    try {
      return localStorage.getItem(PRIMARY_STORAGE_KEY);
    } catch {
      return null;
    }
  });
  const laneRefs = useRef<Map<string, HTMLElement>>(new Map());
  const didRestoreScroll = useRef(false);
  // Shared across every open arc's timeline, so zooming/panning one moves them all together.
  const [timelineZoom, setTimelineZoom] = useState<TimelineZoom>('months');
  const [panDays, setPanDays] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingThought, setEditingThought] = useState<EditableThought | null>(null);
  const [ideaSeed, setIdeaSeed] = useState<Partial<EditableThought> | null>(null);
  const [contentForm, setContentForm] = useState<{
    arcSlug: string;
    thoughtId: string;
    title: string;
    summary: string;
    scheduledFor: string;
    reopenDayIso?: string;
  } | null>(null);
  const [contentSaving, setContentSaving] = useState(false);
  const [siteModalOpen, setSiteModalOpen] = useState(false);
  const [siteDetail, setSiteDetail] = useState<SiteContentDetail | null>(null);
  const [dayModal, setDayModal] = useState<{
    dayIso: string;
    ids: string[];
    arcSlug: string;
  } | null>(null);
  const [stagePicker, setStagePicker] = useState<{
    arcSlug: string;
    arcLabel: string;
    stage: Stage;
  } | null>(null);
  const todayMs = useMemo(() => Date.now(), []);

  useEffect(() => {
    let active = true;
    let channel: ReturnType<typeof supabase.channel> | null = null;

    async function loadNodes(mapId: string) {
      const { data: nodeRows, error: nodesError } = await supabase
        .from('map_nodes')
        .select('id, slug, label, role_description, usage_notes, annotations, y')
        .eq('map_id', mapId)
        .order('y', { ascending: true });
      if (nodesError) throw nodesError;
      if (active) setNodes((nodeRows ?? []) as ArcNodeRow[]);
    }

    async function loadThoughts() {
      const { data, error } = await supabase
        .from('thoughts')
        .select(
          'id, title, seed_idea, reference, arc_slug, arc_stage, arc_placement, arc_fit_note, first_published_at, status, created_at',
        )
        .not('arc_slug', 'is', null)
        .neq('status', 'dead')
        .order('created_at', { ascending: true });
      if (error) throw error;
      if (active) setThoughts((data ?? []) as ThoughtArcRow[]);
    }

    async function loadSiteContent() {
      const { data, error } = await supabase
        .from('site_content')
        .select(SITE_CONTENT_ARC_SELECT)
        .or('arc_slug.not.is.null,source_thought_id.not.is.null')
        .order('pub_date', { ascending: true });
      if (error) throw error;
      if (active) setSiteRows((data ?? []) as SiteContentArcRow[]);
    }

    async function init() {
      setLoading(true);
      try {
        const { data: board, error: boardError } = await supabase.from('system_maps').select('id').eq('slug', BOARD_SLUG).single();
        if (boardError) throw boardError;

        await Promise.all([loadNodes(board.id), loadThoughts(), loadSiteContent()]);

        // Live: arc metadata, thought, or site_content assignment changes re-pull without a manual refresh.
        channel = supabase
          .channel(`narrative-arcs:${board.id}`)
          .on(
            'postgres_changes',
            { event: '*', schema: 'public', table: 'map_nodes', filter: `map_id=eq.${board.id}` },
            () => {
              void loadNodes(board.id);
            }
          )
          .on('postgres_changes', { event: '*', schema: 'public', table: 'thoughts' }, () => {
            void loadThoughts();
          })
          .on('postgres_changes', { event: '*', schema: 'public', table: 'site_content' }, () => {
            void loadSiteContent();
          })
          .subscribe((status) => {
            if (active) setLive(status === 'SUBSCRIBED');
          });
      } catch (e: unknown) {
        toast.error(e instanceof Error ? e.message : 'Failed to load narrative arcs');
      } finally {
        if (active) setLoading(false);
      }
    }

    void init();
    return () => {
      active = false;
      if (channel) void supabase.removeChannel(channel);
    };
  }, []);

  const arcMaterials = useMemo(() => {
    const idea = new Map<string, ArcElement[]>();
    const timeline = new Map<string, ArcElement[]>();
    for (const node of nodes) {
      const forArcThoughts = thoughts.filter((t) => t.arc_slug === node.slug);
      const thoughtIds = new Set(forArcThoughts.map((t) => t.id));
      const forArcSite = siteRows.filter(
        (r) =>
          r.arc_slug === node.slug ||
          (r.source_thought_id != null && thoughtIds.has(r.source_thought_id)),
      );
      idea.set(node.slug, mergeArcElements(forArcThoughts, forArcSite, 'idea'));
      timeline.set(node.slug, mergeArcElements(forArcThoughts, forArcSite, 'calendar'));
    }
    return { idea, timeline };
  }, [nodes, thoughts, siteRows]);

  const elementsBySlug = useMemo(() => {
    if (mode === 'calendar' && !isDetail) return arcMaterials.timeline;
    return arcMaterials.idea;
  }, [arcMaterials, mode, isDetail]);

  const coverageBySlug = useMemo(() => {
    const map = new Map<string, 'unconfirmed' | 'holding' | 'framed_unshipped'>();
    for (const node of nodes) {
      const forArcThoughts = thoughts.filter((t) => t.arc_slug === node.slug);
      const thoughtIds = new Set(forArcThoughts.map((t) => t.id));
      const forArcSite = siteRows.filter(
        (r) =>
          r.arc_slug === node.slug ||
          (r.source_thought_id != null && thoughtIds.has(r.source_thought_id)),
      );
      map.set(node.slug, suggestMaturityFromCoverage(forArcThoughts, forArcSite));
    }
    return map;
  }, [nodes, thoughts, siteRows]);

  // Restore primary from URL (?arc=slug) or localStorage once nodes load.
  useEffect(() => {
    if (nodes.length === 0) return;
    const fromUrl = searchParams.get('arc')?.trim() || null;
    const storedPrimary = (() => {
      try {
        return localStorage.getItem(PRIMARY_STORAGE_KEY);
      } catch {
        return null;
      }
    })();
    const candidate =
      (fromUrl && nodes.some((n) => n.slug === fromUrl) && fromUrl) ||
      (storedPrimary && nodes.some((n) => n.slug === storedPrimary) && storedPrimary) ||
      null;
    if (!candidate) return;
    setPrimarySlug((prev) => (prev === candidate ? prev : candidate));
    setExpanded((prev) => {
      if (prev.has(candidate)) return prev;
      const next = new Set(prev);
      next.add(candidate);
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot restore when board loads
  }, [nodes]);

  useEffect(() => {
    try {
      localStorage.setItem(EXPANDED_STORAGE_KEY, JSON.stringify([...expanded]));
      if (primarySlug) localStorage.setItem(PRIMARY_STORAGE_KEY, primarySlug);
    } catch {
      /* ignore */
    }
  }, [expanded, primarySlug]);

  useEffect(() => {
    if (loading || didRestoreScroll.current || !primarySlug) return;
    const el = laneRefs.current.get(primarySlug);
    if (!el) return;
    didRestoreScroll.current = true;
    requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [loading, primarySlug, expanded, nodes]);

  function toggle(slug: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
    setPrimarySlug(slug);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('arc', slug);
    setSearchParams(nextParams, { replace: true });
  }

  async function openThoughtById(thoughtId: string) {
    const { data, error } = await supabase.from('thoughts').select('*').eq('id', thoughtId).maybeSingle();
    if (error || !data) {
      toast.error(error?.message ?? 'Could not load thought');
      return;
    }
    setSiteModalOpen(false);
    setSiteDetail(null);
    setEditingThought({
      id: String(data.id),
      title: data.title ?? '',
      seed_idea: data.seed_idea ?? '',
      reference: data.reference ?? '',
      status: data.status ?? 'draft',
      final_copy: data.final_copy ?? '',
      arc_slug: data.arc_slug ?? null,
      arc_stage: data.arc_stage ?? null,
      arc_placement: data.arc_placement ?? null,
      arc_fit_note: data.arc_fit_note ?? null,
      first_published_at: data.first_published_at
        ? String(data.first_published_at).slice(0, 10)
        : null,
      created_at: data.created_at,
      updated_at: data.updated_at ?? data.created_at,
    });
    setModalOpen(true);
  }

  async function handleSelectElement(el: ArcElement) {
    if (el.site_content_id) {
      const row = siteRows.find((r) => r.id === el.site_content_id);
      if (!row) {
        toast.error('Could not load site content');
        return;
      }
      const arcLabel = nodes.find((n) => n.slug === row.arc_slug)?.label ?? null;
      setDayModal(null);
      setContentForm(null);
      setSiteDetail(siteDetailFromRow(row, arcLabel));
      setSiteModalOpen(true);
      return;
    }

    if (!el.thought_id) return;
    setDayModal(null);
    setContentForm(null);
    setSiteModalOpen(false);
    setSiteDetail(null);
    await openThoughtById(el.thought_id);
  }

  async function handleUpdateArcMeta(
    nodeId: string,
    patch: { label?: string; role_description?: string | null },
  ) {
    const current = nodes.find((n) => n.id === nodeId);
    if (!current) {
      toast.error('Arc not found');
      return;
    }
    const nextLabel = patch.label !== undefined ? patch.label : current.label;
    const nextDesc =
      patch.role_description !== undefined ? patch.role_description : current.role_description;
    if (nextLabel === current.label && nextDesc === current.role_description) return;

    setNodes((prev) =>
      prev.map((n) =>
        n.id === nodeId
          ? { ...n, label: nextLabel, role_description: nextDesc }
          : n,
      ),
    );
    if (stagePicker?.arcSlug === current.slug && patch.label !== undefined) {
      setStagePicker((prev) => (prev ? { ...prev, arcLabel: nextLabel } : prev));
    }

    const { error } = await supabase
      .from('map_nodes')
      .update({
        ...(patch.label !== undefined ? { label: nextLabel } : {}),
        ...(patch.role_description !== undefined
          ? { role_description: nextDesc }
          : {}),
      })
      .eq('id', nodeId);

    if (error) {
      setNodes((prev) => prev.map((n) => (n.id === nodeId ? current : n)));
      toast.error(error.message);
      throw error;
    }
  }

  async function handleMoveStage(thoughtId: string, stage: Stage) {
    const id = Number(thoughtId);
    const current = thoughts.find((t) => t.id === id);
    if (!current || current.arc_stage === stage) return;

    const linkedPrev = siteRows
      .filter((r) => r.source_thought_id === id)
      .map((r) => ({ id: r.id, arc_stage: r.arc_stage }));

    setThoughts((prev) =>
      prev.map((t) => (t.id === id ? { ...t, arc_stage: stage } : t)),
    );
    setSiteRows((prev) =>
      prev.map((r) => (r.source_thought_id === id ? { ...r, arc_stage: stage } : r)),
    );

    const now = new Date().toISOString();
    const { error } = await supabase
      .from('thoughts')
      .update({
        arc_stage: stage,
        updated_at: now,
      })
      .eq('id', id);

    if (error) {
      toast.error(error.message);
      setThoughts((prev) =>
        prev.map((t) => (t.id === id ? { ...t, arc_stage: current.arc_stage } : t)),
      );
      setSiteRows((prev) =>
        prev.map((r) => {
          const old = linkedPrev.find((x) => x.id === r.id);
          return old ? { ...r, arc_stage: old.arc_stage } : r;
        }),
      );
      return;
    }

    // Keep linked content chips in sync with the idea’s stage.
    const { error: contentErr } = await supabase
      .from('site_content')
      .update({ arc_stage: stage, updated_at: now })
      .eq('source_thought_id', id);
    if (contentErr) {
      toast.error(contentErr.message);
    } else {
      toast.success(`Moved to ${STAGE_LABEL[stage]}`);
    }
  }

  async function handleReschedule(target: {
    kind: 'content';
    id: string;
    scheduledFor: string;
  }) {
    const current = siteRows.find((r) => r.id === target.id);
    if (!current) {
      toast.error('Content not found');
      return;
    }
    if ((current.status ?? '').toLowerCase() !== 'draft') {
      toast.error('Published content date is locked');
      return;
    }
    if (current.scheduled_for?.slice(0, 10) === target.scheduledFor) return;

    const previous = current.scheduled_for;
    setSiteRows((prev) =>
      prev.map((r) =>
        r.id === target.id ? { ...r, scheduled_for: target.scheduledFor } : r,
      ),
    );

    const { error, data } = await supabase
      .from('site_content')
      .update({
        scheduled_for: target.scheduledFor,
        updated_at: new Date().toISOString(),
      })
      .eq('id', target.id)
      .eq('status', 'draft')
      .select('id')
      .maybeSingle();

    if (error || !data) {
      setSiteRows((prev) =>
        prev.map((r) => (r.id === target.id ? { ...r, scheduled_for: previous } : r)),
      );
      toast.error(error?.message ?? 'Could not reschedule — only drafts move');
      return;
    }
    toast.success(`Scheduled ${target.scheduledFor}`);
    if (dayModal && dayModal.dayIso !== target.scheduledFor) {
      setDayModal((prev) =>
        prev ? { ...prev, ids: prev.ids.filter((id) => id !== target.id) } : prev,
      );
    }
  }

  function openNewIdea(arcSlug: string, stage: Stage = 'complication') {
    setSiteModalOpen(false);
    setSiteDetail(null);
    setStagePicker(null);
    setDayModal(null);
    setContentForm(null);
    setIdeaSeed({
      arc_slug: arcSlug,
      arc_stage: stage,
      arc_placement: 'tentative',
      status: 'draft',
    });
    setEditingThought(null);
    setModalOpen(true);
  }

  function openStageIdeaPicker(arcSlug: string, arcLabel: string, stage: Stage) {
    setSiteModalOpen(false);
    setSiteDetail(null);
    setDayModal(null);
    setContentForm(null);
    setModalOpen(false);
    setEditingThought(null);
    setIdeaSeed(null);
    setStagePicker({ arcSlug, arcLabel, stage });
  }

  async function attachIdeaToStage(thoughtId: number, arcSlug: string, stage: Stage) {
    const existing = thoughts.find((t) => t.id === thoughtId);
    const previous = existing
      ? { arc_slug: existing.arc_slug, arc_stage: existing.arc_stage }
      : null;

    if (existing) {
      setThoughts((prev) =>
        prev.map((t) =>
          t.id === thoughtId ? { ...t, arc_slug: arcSlug, arc_stage: stage } : t,
        ),
      );
    }

    const now = new Date().toISOString();
    const { data, error } = await supabase
      .from('thoughts')
      .update({
        arc_slug: arcSlug,
        arc_stage: stage,
        updated_at: now,
      })
      .eq('id', thoughtId)
      .select(
        'id, title, seed_idea, reference, arc_slug, arc_stage, arc_placement, arc_fit_note, first_published_at, status, created_at',
      )
      .single();

    if (error || !data) {
      if (existing && previous) {
        setThoughts((prev) =>
          prev.map((t) =>
            t.id === thoughtId
              ? { ...t, arc_slug: previous.arc_slug, arc_stage: previous.arc_stage }
              : t,
          ),
        );
      }
      toast.error(error?.message ?? 'Could not attach idea');
      throw error ?? new Error('attach failed');
    }

    const row = data as ThoughtArcRow;
    setThoughts((prev) => {
      const idx = prev.findIndex((t) => t.id === thoughtId);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = row;
        return next;
      }
      return [...prev, row];
    });
    setSiteRows((prev) =>
      prev.map((r) =>
        r.source_thought_id === thoughtId
          ? { ...r, arc_slug: arcSlug, arc_stage: stage }
          : r,
      ),
    );
    void supabase
      .from('site_content')
      .update({ arc_slug: arcSlug, arc_stage: stage, updated_at: now })
      .eq('source_thought_id', thoughtId);
    toast.success(`Idea → ${STAGE_LABEL[stage]}`);
  }

  function openContentForm(
    arcSlug: string,
    opts?: { scheduledFor?: string; reopenDayIso?: string; thoughtId?: string },
  ) {
    const arcThoughts = thoughts.filter((t) => t.arc_slug === arcSlug);
    const thoughtId =
      opts?.thoughtId && arcThoughts.some((t) => String(t.id) === opts.thoughtId)
        ? opts.thoughtId
        : arcThoughts[0]
          ? String(arcThoughts[0].id)
          : '';
    const thought = thoughts.find((t) => String(t.id) === thoughtId);
    const defaultDate =
      opts?.scheduledFor ||
      (() => {
        const d = new Date();
        d.setDate(d.getDate() + 14);
        return d.toISOString().slice(0, 10);
      })();
    // One overlay at a time — never stack day list / detail / add form.
    setDayModal(null);
    setSiteModalOpen(false);
    setSiteDetail(null);
    setStagePicker(null);
    setModalOpen(false);
    setEditingThought(null);
    setIdeaSeed(null);
    setContentForm({
      arcSlug,
      thoughtId,
      title: '',
      summary: '',
      scheduledFor: defaultDate,
      reopenDayIso: opts?.reopenDayIso,
    });
  }

  async function submitContentForm() {
    if (!contentForm) return;
    const title = contentForm.title.trim();
    if (!title) {
      toast.error('Name is required');
      return;
    }
    if (!contentForm.thoughtId) {
      toast.error('Pick the idea this content belongs to');
      return;
    }
    const thought = thoughts.find((t) => String(t.id) === contentForm.thoughtId);
    if (!thought) {
      toast.error('Idea not found on this arc');
      return;
    }

    const scheduledFor =
      contentForm.scheduledFor ||
      (() => {
        const d = new Date();
        d.setDate(d.getDate() + 14);
        return d.toISOString().slice(0, 10);
      })();

    setContentSaving(true);
    try {
      const slugBase = title
        .toLowerCase()
        .replace(/['’']/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 50);
      const content_id = `${slugBase || 'stub'}-${Date.now().toString(36)}`;
      const now = new Date().toISOString();
      const sameDayCount = siteRows.filter(
        (r) => (r.scheduled_for ?? r.pub_date)?.slice(0, 10) === scheduledFor,
      ).length;
      const { data, error } = await supabase
        .from('site_content')
        .insert([
          {
            collection: 'insights',
            content_id,
            title,
            summary: contentForm.summary.trim() || null,
            url: '',
            body_hash: `engine_stub:${content_id}`,
            status: 'draft',
            origin: 'engine_stub',
            scheduled_for: scheduledFor,
            schedule_order: sameDayCount,
            pub_date: null,
            maturity: 0,
            gated: false,
            arc_slug: contentForm.arcSlug,
            arc_stage: thought.arc_stage ?? 'complication',
            source_thought_id: thought.id,
            created_at: now,
            updated_at: now,
          },
        ])
        .select(SITE_CONTENT_ARC_SELECT)
        .single();

      if (error || !data) throw error ?? new Error('Create failed');

      const created = data as SiteContentArcRow;
      setSiteRows((prev) => [...prev, created]);
      const reopenDay = contentForm.reopenDayIso;
      const arcSlug = contentForm.arcSlug;
      setContentForm(null);
      toast.success('Draft content tied to idea');
      if (reopenDay) {
        const idsOnDay = [
          ...siteRows
            .filter((r) => (r.scheduled_for ?? r.pub_date)?.slice(0, 10) === reopenDay)
            .map((r) => r.id),
          created.id,
        ];
        setDayModal({ dayIso: reopenDay, ids: idsOnDay, arcSlug });
      }
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Could not create content');
    } finally {
      setContentSaving(false);
    }
  }

  async function handleSaveSiteContent(id: string, patch: SiteContentDetailSave) {
    const current = siteRows.find((r) => r.id === id);
    if (!current) {
      toast.error('Could not find site content');
      throw new Error('missing row');
    }

    const isDraft = (current.status ?? '').toLowerCase() === 'draft';
    const nextScheduled = isDraft ? patch.scheduled_for : current.scheduled_for;
    const nextArcLabel = nodes.find((n) => n.slug === patch.arc_slug)?.label ?? null;
    const optimistic: SiteContentArcRow = {
      ...current,
      title: patch.title,
      summary: patch.summary,
      scheduled_for: nextScheduled,
      arc_slug: patch.arc_slug,
      source_thought_id: patch.source_thought_id,
      arc_stage: patch.arc_stage,
    };

    setSiteRows((prev) => prev.map((r) => (r.id === id ? optimistic : r)));
    setSiteDetail(siteDetailFromRow(optimistic, nextArcLabel));

    const updatePayload: Record<string, unknown> = {
      title: patch.title,
      summary: patch.summary,
      arc_slug: patch.arc_slug,
      source_thought_id: patch.source_thought_id,
      arc_stage: patch.arc_stage,
      updated_at: new Date().toISOString(),
    };
    if (isDraft) updatePayload.scheduled_for = nextScheduled;

    const { error } = await supabase.from('site_content').update(updatePayload).eq('id', id);

    if (error) {
      setSiteRows((prev) => prev.map((r) => (r.id === id ? current : r)));
      const prevLabel = nodes.find((n) => n.slug === current.arc_slug)?.label ?? null;
      setSiteDetail(siteDetailFromRow(current, prevLabel));
      toast.error(error.message);
      throw error;
    }
    toast.success('Content updated');
  }

  async function handleDayReorder(orderedIds: string[]) {
    setDayModal((prev) => (prev ? { ...prev, ids: orderedIds } : prev));
    setSiteRows((prev) =>
      prev.map((r) => {
        const idx = orderedIds.indexOf(r.id);
        return idx >= 0 ? { ...r, schedule_order: idx } : r;
      }),
    );
    const now = new Date().toISOString();
    const results = await Promise.all(
      orderedIds.map((id, i) =>
        supabase
          .from('site_content')
          .update({ schedule_order: i, updated_at: now })
          .eq('id', id),
      ),
    );
    const firstError = results.find((r) => r.error)?.error;
    if (firstError) toast.error(firstError.message);
  }

  const dayModalItems = useMemo((): TimelineDayItem[] => {
    if (!dayModal) return [];
    const byId = new Map(siteRows.map((r) => [r.id, r]));
    return dayModal.ids
      .map((id) => byId.get(id))
      .filter((r): r is SiteContentArcRow => Boolean(r))
      .map((r) => ({
        id: r.id,
        title: r.title?.trim() || r.content_id,
        summary: r.summary,
        status: r.status,
        scheduled_for: r.scheduled_for,
        pub_date: r.pub_date,
        url: r.url,
      }));
  }, [dayModal, siteRows]);

  useEffect(() => {
    if (dayModal && dayModal.ids.length === 0) setDayModal(null);
  }, [dayModal]);

  const visibleNodes = useMemo(() => {
    if (!focusSlug?.trim()) return nodes;
    return nodes.filter((n) => n.slug === focusSlug.trim());
  }, [nodes, focusSlug]);

  return (
    <div className={`w-full ${isDetail ? '-mt-1' : ''}`}>
      <header
        className={`flex items-start justify-between gap-4 flex-wrap ${
          isDetail ? 'mb-1.5' : 'mb-5'
        }`}
      >
        <div>
          {isDetail ? (
            <div className="flex items-center gap-2 flex-wrap text-[12px]">
              <Link
                to="/narrative-arcs"
                className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:underline"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                All arcs
              </Link>
              <span className="text-gray-300 dark:text-gray-600">·</span>
              <span className="uppercase tracking-wide text-[10px] text-gray-400 dark:text-gray-500">
                Arc canvas
              </span>
              <span className="text-gray-300 dark:text-gray-600">·</span>
              <span className="inline-flex items-center gap-1.5 text-gray-500 dark:text-gray-400">
                <span
                  className={`w-1.5 h-1.5 rounded-full ${live ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-gray-600'}`}
                />
                {live ? 'Live' : 'Connecting…'}
              </span>
            </div>
          ) : (
            <>
              <h1 className="text-2xl sm:text-[28px] font-semibold text-gray-900 dark:text-white tracking-tight">
                Narrative Arcs
              </h1>
              <div className="flex items-center gap-2.5 flex-wrap text-[13px] text-gray-500 dark:text-gray-400 mt-1.5">
                <span className="uppercase tracking-wide text-[11px] text-gray-400 dark:text-gray-500">
                  Reviewed weekly
                </span>
                <span className="text-gray-300 dark:text-gray-600">·</span>
                <span>Open canvas for full space</span>
                <span className="text-gray-300 dark:text-gray-600">·</span>
                <span className="inline-flex items-center gap-1.5">
                  <span
                    className={`w-1.5 h-1.5 rounded-full ${live ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-gray-600'}`}
                  />
                  {live ? 'Live' : 'Connecting…'}
                </span>
              </div>
            </>
          )}
        </div>
        {!isDetail && <ModeToggle mode={mode} onChange={setMode} />}
      </header>

      {loading ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>
      ) : visibleNodes.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {isDetail ? 'Arc not found.' : 'No arcs yet.'}
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          {visibleNodes.map((node) => (
            <ArcLane
              key={node.id}
              node={node}
              elements={
                isDetail
                  ? (arcMaterials.idea.get(node.slug) ?? [])
                  : (elementsBySlug.get(node.slug) ?? [])
              }
              timelineElements={arcMaterials.timeline.get(node.slug) ?? []}
              todayMs={todayMs}
              mode={mode}
              timelineZoom={timelineZoom}
              onTimelineZoomChange={setTimelineZoom}
              panDays={panDays}
              onPanDaysChange={setPanDays}
              expanded={isDetail || expanded.has(node.slug)}
              primary={primarySlug === node.slug || isDetail}
              onToggle={() => toggle(node.slug)}
              onSelectElement={handleSelectElement}
              onOpenDayCluster={(dayIso, items) => {
                setContentForm(null);
                setSiteModalOpen(false);
                setSiteDetail(null);
                setDayModal({
                  dayIso,
                  arcSlug: node.slug,
                  ids: items
                    .map((el) => el.site_content_id)
                    .filter((id): id is string => Boolean(id)),
                });
              }}
              onAddContentForDay={(dayIso) => {
                openContentForm(node.slug, { scheduledFor: dayIso });
              }}
              onMoveStage={handleMoveStage}
              onReschedule={handleReschedule}
              onAddToStage={
                isDetail
                  ? (stage) => openStageIdeaPicker(node.slug, node.label, stage)
                  : undefined
              }
              onAddContentForIdea={
                isDetail
                  ? (thoughtId) =>
                      openContentForm(node.slug, { thoughtId })
                  : undefined
              }
              onUpdateMeta={
                isDetail
                  ? (patch) => handleUpdateArcMeta(node.id, patch)
                  : undefined
              }
              coverageHint={coverageBySlug.get(node.slug)}
              layout={isDetail ? 'detail' : 'list'}
              sectionRef={(el) => {
                if (el) laneRefs.current.set(node.slug, el);
                else laneRefs.current.delete(node.slug);
              }}
            />
          ))}
        </div>
      )}

      {!isDetail && (
        <footer className="mt-10 pt-4 border-t border-gray-200 dark:border-gray-700 text-[12.5px] leading-relaxed text-gray-400 dark:text-gray-500">
          Collapsed rows show idea maturity + content dots. Open canvas for drag/stages and full timeline.
          Hover → Open idea or Open arc canvas.
        </footer>
      )}

      <ThoughtModal
        isOpen={modalOpen}
        thought={editingThought}
        seed={ideaSeed}
        variant="arc"
        onClose={() => {
          setModalOpen(false);
          setEditingThought(null);
          setIdeaSeed(null);
        }}
        onSuccess={() => {
          setIdeaSeed(null);
          void supabase
            .from('thoughts')
            .select(
              'id, title, seed_idea, reference, arc_slug, arc_stage, arc_placement, arc_fit_note, first_published_at, status, created_at',
            )
            .not('arc_slug', 'is', null)
            .neq('status', 'dead')
            .order('created_at', { ascending: true })
            .then(({ data }) => setThoughts((data ?? []) as ThoughtArcRow[]));
          void supabase
            .from('site_content')
            .select(SITE_CONTENT_ARC_SELECT)
            .or('arc_slug.not.is.null,source_thought_id.not.is.null')
            .order('pub_date', { ascending: true })
            .then(({ data }) => setSiteRows((data ?? []) as SiteContentArcRow[]));
        }}
      />

      <StageIdeaPickerModal
        isOpen={Boolean(stagePicker)}
        arcSlug={stagePicker?.arcSlug ?? ''}
        arcLabel={stagePicker?.arcLabel ?? ''}
        stage={stagePicker?.stage ?? 'complication'}
        stageLabel={stagePicker ? STAGE_LABEL[stagePicker.stage] : ''}
        occupiedThoughtIds={
          new Set(
            stagePicker
              ? thoughts
                  .filter(
                    (t) =>
                      t.arc_slug === stagePicker.arcSlug &&
                      t.arc_stage === stagePicker.stage,
                  )
                  .map((t) => t.id)
              : [],
          )
        }
        onClose={() => setStagePicker(null)}
        onCreateNew={() => {
          if (!stagePicker) return;
          openNewIdea(stagePicker.arcSlug, stagePicker.stage);
        }}
        onAttach={async (thoughtId) => {
          if (!stagePicker) return;
          await attachIdeaToStage(thoughtId, stagePicker.arcSlug, stagePicker.stage);
        }}
      />

      <SiteContentDetailModal
        isOpen={siteModalOpen}
        detail={siteDetail}
        arcs={nodes.map((n) => ({ slug: n.slug, label: n.label }))}
        ideas={thoughts.map((t) => ({
          id: t.id,
          title: t.title,
          arc_slug: t.arc_slug,
          arc_stage: t.arc_stage,
        }))}
        onClose={() => {
          setSiteModalOpen(false);
          setSiteDetail(null);
        }}
        onSave={handleSaveSiteContent}
        onOpenThought={(thoughtId) => {
          void openThoughtById(thoughtId);
        }}
      />

      <TimelineDayModal
        isOpen={Boolean(dayModal) && !contentForm && !siteModalOpen}
        dayIso={dayModal?.dayIso ?? ''}
        items={dayModalItems}
        onClose={() => setDayModal(null)}
        onChangeDate={(id, scheduledFor) =>
          void handleReschedule({ kind: 'content', id, scheduledFor })
        }
        onReorder={(orderedIds) => void handleDayReorder(orderedIds)}
        onAddContent={() => {
          if (!dayModal) return;
          openContentForm(dayModal.arcSlug, {
            scheduledFor: dayModal.dayIso,
            reopenDayIso: dayModal.dayIso,
          });
        }}
        onOpenContent={(id) => {
          const row = siteRows.find((r) => r.id === id);
          if (!row) return;
          // One modal at a time — close day list before opening detail.
          setDayModal(null);
          setContentForm(null);
          const arcLabel = nodes.find((n) => n.slug === row.arc_slug)?.label ?? null;
          setSiteDetail(siteDetailFromRow(row, arcLabel));
          setSiteModalOpen(true);
        }}
      />

      {contentForm && (
        <div
          className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="content-placeholder-title"
          onClick={() => !contentSaving && setContentForm(null)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-sm p-4 space-y-3"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-3">
              <h3
                id="content-placeholder-title"
                className="text-sm font-semibold text-gray-900 dark:text-white"
              >
                Content placeholder
              </h3>
              <button
                type="button"
                className="text-xs text-gray-500 hover:underline"
                disabled={contentSaving}
                onClick={() => setContentForm(null)}
              >
                Cancel
              </button>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                Name
              </label>
              <input
                type="text"
                autoFocus
                className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900"
                value={contentForm.title}
                onChange={(e) => setContentForm({ ...contentForm, title: e.target.value })}
                placeholder="Short title"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    void submitContentForm();
                  }
                }}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                Short description
              </label>
              <textarea
                className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 min-h-[56px] resize-none"
                value={contentForm.summary}
                onChange={(e) => setContentForm({ ...contentForm, summary: e.target.value })}
                placeholder="One or two sentences"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                Tie to idea
              </label>
              <select
                className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900"
                value={contentForm.thoughtId}
                onChange={(e) =>
                  setContentForm({ ...contentForm, thoughtId: e.target.value })
                }
              >
                <option value="">Select idea…</option>
                {thoughts
                  .filter((t) => t.arc_slug === contentForm.arcSlug)
                  .map((t) => (
                    <option key={t.id} value={String(t.id)}>
                      {t.title?.trim() || `Idea #${t.id}`}
                    </option>
                  ))}
              </select>
              {thoughts.filter((t) => t.arc_slug === contentForm.arcSlug).length === 0 && (
                <p className="mt-1 text-[11px] text-amber-600 dark:text-amber-400">
                  Add an idea on this arc first.
                </p>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                Schedule date
              </label>
              <input
                type="date"
                className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900"
                value={contentForm.scheduledFor}
                onChange={(e) =>
                  setContentForm({ ...contentForm, scheduledFor: e.target.value })
                }
              />
              <p className="mt-1 text-[11px] text-gray-400 dark:text-gray-500">
                Links to this arc · idea above
              </p>
            </div>
            <button
              type="button"
              disabled={contentSaving}
              onClick={() => void submitContentForm()}
              className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm rounded-md bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
            >
              <FilePlus2 className="w-4 h-4" />
              {contentSaving ? 'Saving…' : 'Add draft'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
